// frontend/src/components/StageView.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IoShareOutline } from "react-icons/io5";

import ContextMenu from './ContextMenu';
import SectionList from './SectionList';
import MusicSpectrogram from './MusicSpectrogram';
import Tooltip from './Tooltip';
import StuntGroupList from './StuntGroupList';

import DancerService from '../services/DancerService';
import SkillService from '../services/SkillService';
import StuntService from '../services/StuntService';
import PositionService from '../services/PositionService';
import LinkService from '../services/LinkService';

// Nouveaux imports - Hooks
import { useWindowSize } from '../hooks/useWindowSize';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { useLinkMode } from '../hooks/useLinkMode';
import { useWebSocket } from '../hooks/useWebSocket';
import wsAction from '../utils/wsActions';

// Nouveaux imports - Utils
import {
  TOP_BAR_HEIGHT,
  CONTROL_BAR_HEIGHT,
  BOTTOM_BAR_HEIGHT,
  CANVAS_BASE_WIDTH,
  CANVAS_BASE_HEIGHT,
  CANVAS_RATIO,
  SIDEBAR_WIDTH,
  MIN_MARGIN,
  THRESHOLD,
  skillColors,
  timelineHeight,
  amplitudeSliderWidth,
} from '../utils/constants';
import { formatTimestamp, calculateCanvasDimensions } from '../utils/helpers';
import {
  drawScene as drawSceneUtil,
  drawGrid,
  drawPosition,
  drawTrajectory as drawTrajectoryUtil,
  afficherInterpolationPositions as afficherInterpolationPositionsUtil,
} from '../utils/canvasDrawing';
import {
  lancerAnimationVersProchainePeriode as lancerAnimationVersProchainePeriodeUtil,
  stopAnimation,
} from '../utils/animationController';
import { generateChoreographyPDF } from '../utils/pdfGenerator';

// Nouveaux imports - API
import { sessionApi } from '../api/sessionApi';

// Nouveaux imports - UI Components
import OptimizationOverlay from './StageView/OptimizationOverlay';
import ErrorDisplay from './StageView/ErrorDisplay';
import CursorModeSelector from './StageView/CursorModeSelector';
import TopBar from './StageView/TopBar';
import RemoteCursors from './StageView/RemoteCursors';

// Images (conservées)
import defaultCursor from '../images/defaultCursor.svg';
import trashIcon from '../images/trashIcon.svg';
import { ReactComponent as DownloadIcon } from '../images/downloadIcon.svg';
import { ReactComponent as SettingsIcon } from '../images/settingsIcon.svg';
import { ReactComponent as WarningIcon } from '../images/warningIcon.svg';
import { ReactComponent as ClosedLock } from '../images/closedLock.svg';
import { ReactComponent as OpenedLock } from '../images/openedLock.svg';

// Feuille de style
import '../styles/StageView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * StageView
 */
export default function StageView({ user }) {
  const { sessionId } = useParams();

  // -----------------------------
  // States
  // -----------------------------
  const [currentSession, setCurrentSession] = useState(null);
  const [dancers, setDancers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [skills, setSkills] = useState([]);
  const [stunts, setStunts] = useState([]);

  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [selectedDancerId, setSelectedDancerId] = useState(null);
  const [selectedSkillId, setSelectedSkillId] = useState(null);

  const [newPeriodName, setNewPeriodName] = useState('');
  const [newDancerName, setNewDancerName] = useState('');
  const [newSkillName, setNewSkillName] = useState('');

  // Share modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [sentInvitations, setSentInvitations] = useState([]);

  // Tri des listes (default, asc, desc)
  const [dancersSort, setDancersSort] = useState('default');
  const [skillsSort, setSkillsSort] = useState('default');
  const [stuntGroupsSort, setStuntGroupsSort] = useState('default');

  // Messages d'erreur
  const [sessionErrors, setSessionErrors] = useState([]);
  const [showErrorMenu, setShowErrorMenu] = useState(false);

  // Playing
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(1); // 1 correspond à 100%

  // Déclarer une référence pour mémoriser le dernier état "inAPeriod"
  const prevInPeriod = useRef(null);
  const prevIsPlaying = useRef(false); // ✅ Initialiser avec false au lieu de isPlaying
  const pausedTime = useRef(null); // ✅ Mémoriser le temps au moment de la pause
  const isUpdatingPeriod = useRef(false); // ✅ Flag pour désactiver l'interpolation pendant la mise à jour d'une période

  // Controlbar states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isThreeDMode, setIsThreeDMode] = useState(false);

  // Optimisation 
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [showOptimizationSuccess, setShowOptimizationSuccess] = useState(false);
  const [optimizationError, setOptimizationError] = useState(null); // Message d'erreur d'optimisation
  const [forcedPosition, setForcedPosition] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const optimizationAbortControllerRef = useRef(null);
  const optimizationTimerRef = useRef(null);
  const errorTimerRef = useRef(null);
  
  // Musique  
  const [musicIsDragging, setMusicIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Skill color
  const skillColors = [
    "#36949d", "#ff924c", "#1982c4", "#ffca3a",
    "#4267ac", "#c5ca30", "#565aa0", "#8ac926", "#6a4c93",
    "#e97c61", "#e9c46a", "#e76f51", "#8ab17d", "#ee8959",
    "#2a9d8f", "#f4a261", "#287271", "#efb366", "#264653", "#ff595e"
  ];
  const [colorIndex, setColorIndex] = useState(0);
  const [newSkillColor, setNewSkillColor] = useState(skillColors[colorIndex]); // Initialisation

  //
  const canvasRef = useRef(null);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 }); // Position du canvas pour les curseurs distants
  const [canvasRealSize, setCanvasRealSize] = useState({ width: 0, height: 0 }); // Taille réelle du canvas à l'écran

  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPosId, setDraggedPosId] = useState(null);

  const [pendingClickInfo, setPendingClickInfo] = useState(null);
  const THRESHOLD = 5;

  // Vue "scene" ou "trajectory"
  const [viewMode, setViewMode] = useState('scene');

  const navigate = useNavigate();

  // -----------------------------
  // Fonctions pour les invitations
  // -----------------------------
  const fetchSentInvitations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/invitations/sent`, {
        headers: { 'Authorization': `Bearer ${user.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSentInvitations(data.filter(inv => inv.session_id === parseInt(sessionId)));
      }
    } catch (error) {
      console.error("Erreur lors du chargement des invitations :", error);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.access_token}` }
      });
      if (response.ok) {
        setSentInvitations(sentInvitations.filter(inv => inv.id !== invitationId));
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation de l'invitation :", error);
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) return;
    
    setInviteError('');
    setInviteSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({ 
          session_id: parseInt(sessionId), 
          invitee_email: inviteEmail 
        }),
      });
      
      if (response.ok) {
        setInviteSuccess('Invitation envoyée !');
        setInviteEmail('');
        fetchSentInvitations(); // Rafraîchir la liste
        setTimeout(() => setInviteSuccess(''), 3000);
      } else {
        const data = await response.json();
        const detail = data.detail;
        if (typeof detail === 'string') {
          setInviteError(detail);
        } else if (Array.isArray(detail)) {
          setInviteError(detail.map(d => d.msg || d).join(', '));
        } else if (detail?.msg) {
          setInviteError(detail.msg);
        } else {
          setInviteError('Erreur lors de l\'envoi');
        }
      }
    } catch (error) {
      setInviteError('Erreur de connexion');
    }
  };

  // Fonction de tri alphabétique
  const sortData = (data, sortMode) => {
    if (sortMode === 'default') return data;
    return [...data].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (sortMode === 'asc') return nameA.localeCompare(nameB);
      return nameB.localeCompare(nameA);
    });
  };

  // Composant bouton de tri
  const SortButton = ({ sortMode, setSortMode }) => {
    const cycleSort = () => {
      if (sortMode === 'default') setSortMode('asc');
      else if (sortMode === 'asc') setSortMode('desc');
      else setSortMode('default');
    };

    return (
      <button
        onClick={cycleSort}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 6px',
          fontSize: '0.75rem',
          color: sortMode === 'default' ? '#999' : '#333',
          fontWeight: sortMode === 'default' ? 'normal' : '600',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e0e0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={sortMode === 'default' ? 'Tri par défaut' : sortMode === 'asc' ? 'A → Z' : 'Z → A'}
      >
        {sortMode === 'default' ? (
          <>A-Z<span style={{ fontSize: '0.7rem' }}>•</span></>
        ) : sortMode === 'asc' ? (
          <>A-Z<span style={{ fontSize: '0.6rem' }}>↓</span></>
        ) : (
          <>Z-A<span style={{ fontSize: '0.6rem' }}>↑</span></>
        )}
      </button>
    );
  };

  // -----------------------------
  // Dimensions dynamiques
  // -----------------------------
  const windowSize = useWindowSize();

  // 1) Espace horizontal disponible (fenêtre - sidebar - marges)
  const availableWidth = windowSize.width - SIDEBAR_WIDTH - 2 * MIN_MARGIN;

  const [cursorMode, setCursorMode] = useState('default');

  // ✅ Hook pour gérer le mode liaison
  const {
    linkChain,
    isLinkModeActive,
    togglePositionInChain,
    isPositionInChain,
    clearLinkChain,
    setLinkChainDirectly,
    getPositionsByLinkGroup,
    linkError,
    linkErrorVisible,
    closeLinkError,
    showLinkError,
  } = useLinkMode({ cursorMode, allPeriods: periods });

  // Etats pour le menu contextuel (clic droit) sur le danseur côté volet gauche
  const [dancerContextMenu, setDancerContextMenu] = useState({
     x: 0,
     y: 0,
     visible: false,
     dancerId: null,
     options: [],
   });

  // Tooltip state
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: '',
  });
  const tooltipTimeoutRef = useRef(null);

  // ✅ État pour gérer le survol du message d'info en mode link
  const [isLinkInfoHovered, setIsLinkInfoHovered] = useState(false);

  // ✅ État pour les curseurs distants (collaboration temps réel)
  const [remoteCursors, setRemoteCursors] = useState({}); // { [userId]: { x, y, period_id, user_info } }
  const currentPeriodIdRef = useRef(null); // Ref pour accéder à currentPeriodId dans le callback

  // ✅ WebSocket pour la collaboration temps réel
  const handleWebSocketMessage = (message) => {
    // ✅ Traitement spécial pour les curseurs (haute fréquence, pas de log)
    if (message.type === 'cursor_moved') {
      const isOnSamePeriod = message.period_id === currentPeriodIdRef.current;
      
      setRemoteCursors(prev => {
        // Si sur une période différente, stocker seulement le period_id pour l'optimisation
        if (!isOnSamePeriod) {
          // Si on avait déjà les infos complètes et il change de période, garder user_info
          const existingUserInfo = prev[message.user_id]?.user_info;
          return {
            ...prev,
            [message.user_id]: {
              period_id: message.period_id,
              view_mode: message.view_mode,
              user_info: existingUserInfo || message.user_info,
              timestamp: Date.now()
            }
          };
        }
        
        // Sur la même période : stocker toutes les infos
        return {
          ...prev,
          [message.user_id]: {
            x: message.x,
            y: message.y,
            period_id: message.period_id,
            view_mode: message.view_mode,
            user_info: message.user_info,
            timestamp: Date.now()
          }
        };
      });
      return; // Skip le reste du traitement
    }

    // ✅ Nettoyer le curseur quand un utilisateur quitte
    if (message.type === 'user_left') {
      setRemoteCursors(prev => {
        const next = { ...prev };
        delete next[message.user_id];
        return next;
      });
    }

    console.log('🔔 Message WebSocket dans StageView:', message.type);
    
    // ✅ Traiter les réponses aux requêtes (avec request_id) mais continuer pour maj UI
    wsAction.handleResponse(message);
    
    // ✅ Mettre à jour l'état local (pour l'émetteur ET les broadcasts)
    switch (message.type) {
      case 'position_updated':
        // Mettre à jour la position dans currentSession
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.id === message.position.id ? { ...pos, ...message.position } : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.id === message.position.id ? { ...pos, ...message.position } : pos
          )
        })));
        break;
        
      case 'position_created':
        // Ajouter la nouvelle position
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period =>
            period.id === message.position.period_id
              ? { ...period, positions: [...(period.positions || []), message.position] }
              : period
          );
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period =>
          period.id === message.position.period_id
            ? { ...period, positions: [...(period.positions || []), message.position] }
            : period
        ));
        break;
        
      case 'position_deleted':
        // Supprimer la position
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.filter(pos => pos.id !== message.position_id)
          }));
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.filter(pos => pos.id !== message.position_id)
        })));
        break;
        
      case 'position_dancer_assigned':
        // Mettre à jour l'assignation dancer dans la position
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.id === message.position_id ? { ...pos, dancer_id: message.dancer_id } : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.id === message.position_id ? { ...pos, dancer_id: message.dancer_id } : pos
          )
        })));
        break;
        
      case 'dancer_created':
        setDancers(prev => [...prev, message.dancer]);
        setCurrentSession(prev => prev ? { ...prev, dancers: [...prev.dancers, message.dancer] } : prev);
        break;
        
      case 'dancer_updated':
        setDancers(prev => prev.map(d => d.id === message.dancer.id ? message.dancer : d));
        setCurrentSession(prev => prev ? { ...prev, dancers: prev.dancers.map(d => d.id === message.dancer.id ? message.dancer : d) } : prev);
        break;
        
      case 'dancer_deleted':
        setDancers(prev => prev.filter(d => d.id !== message.dancer_id));
        setCurrentSession(prev => prev ? { ...prev, dancers: prev.dancers.filter(d => d.id !== message.dancer_id) } : prev);
        break;
        
      case 'skill_created':
        setSkills(prev => [...prev, message.skill]);
        setCurrentSession(prev => prev ? { ...prev, skills: [...prev.skills, message.skill] } : prev);
        break;
        
      case 'skill_updated':
        setSkills(prev => prev.map(s => s.id === message.skill.id ? message.skill : s));
        setCurrentSession(prev => prev ? { ...prev, skills: prev.skills.map(s => s.id === message.skill.id ? message.skill : s) } : prev);
        break;
        
      case 'skill_deleted':
        setSkills(prev => prev.filter(s => s.id !== message.skill_id));
        setCurrentSession(prev => prev ? { ...prev, skills: prev.skills.filter(s => s.id !== message.skill_id) } : prev);
        break;
        
      case 'period_created':
        setPeriods(prev => {
          const updated = [...prev, message.period];
          return updated.sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
        });
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updated = [...prev.periods, message.period];
          const sorted = updated.sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
          
          // Mettre à jour l'index si la période sélectionnée a changé de position
          if (currentPeriodIdRef.current) {
            const newIndex = sorted.findIndex(p => p.id === currentPeriodIdRef.current);
            if (newIndex !== -1) {
              setCurrentPeriodIndex(newIndex);
            }
          }
          
          return { ...prev, periods: sorted };
        });
        break;
        
      case 'period_updated':
        // Fusionner les données, re-trier par start_time, et ajuster l'index sélectionné
        setPeriods(prev => {
          const updated = prev.map(p => p.id === message.period.id ? { ...p, ...message.period, positions: message.period.positions || p.positions } : p);
          return updated.sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
        });
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updated = prev.periods.map(p => p.id === message.period.id ? { ...p, ...message.period, positions: message.period.positions || p.positions } : p);
          const sorted = updated.sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
          
          // Trouver le nouvel index de la période actuellement sélectionnée (basé sur l'ID)
          if (currentPeriodIdRef.current) {
            const newIndex = sorted.findIndex(p => p.id === currentPeriodIdRef.current);
            if (newIndex !== -1) {
              setCurrentPeriodIndex(newIndex);
            }
          }
          
          return { ...prev, periods: sorted };
        });
        break;
        
      case 'period_deleted':
        setPeriods(prev => prev.filter(p => p.id !== message.period_id));
        setCurrentSession(prev => {
          if (!prev) return prev;
          const deletedIndex = prev.periods.findIndex(p => p.id === message.period_id);
          const updatedPeriods = prev.periods.filter(p => p.id !== message.period_id);
          
          // Ajuster l'index sélectionné si une période avant celle sélectionnée est supprimée
          setCurrentPeriodIndex(currentIdx => {
            if (deletedIndex !== -1 && deletedIndex < currentIdx) {
              return Math.max(0, currentIdx - 1);
            } else if (deletedIndex === currentIdx) {
              // Si la période sélectionnée est supprimée, sélectionner la précédente ou 0
              return Math.max(0, deletedIndex - 1);
            }
            return currentIdx;
          });
          
          return { ...prev, periods: updatedPeriods };
        });
        break;
        
      case 'position_skill_associated':
      case 'position_skill_removed':
        // Mettre à jour la position avec les skills
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.id === message.position.id ? message.position : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.id === message.position.id ? message.position : pos
          )
        })));
        break;
        
      case 'dancer_skill_associated':
      case 'dancer_skill_removed':
        // Mettre à jour le dancer avec les skills
        setDancers(prev => prev.map(d => d.id === message.dancer.id ? message.dancer : d));
        setCurrentSession(prev => prev ? { ...prev, dancers: prev.dancers.map(d => d.id === message.dancer.id ? message.dancer : d) } : prev);
        break;
        
      case 'position_stunt_associated':
      case 'position_stunt_removed':
        // Mettre à jour la position avec les stunts
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.id === message.position.id ? message.position : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        // Aussi mettre à jour periods pour déclencher le recalcul des erreurs
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.id === message.position.id ? message.position : pos
          )
        })));
        break;
        
      case 'link_created':
        // Mettre à jour les positions avec le nouveau link_group_id
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              message.position_ids.includes(pos.id) ? { ...pos, link_group_id: message.link_group_id } : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            message.position_ids.includes(pos.id) ? { ...pos, link_group_id: message.link_group_id } : pos
          )
        })));
        break;
        
      case 'link_deleted':
        // Retirer le link_group_id des positions
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.link_group_id === message.link_group_id ? { ...pos, link_group_id: null } : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.link_group_id === message.link_group_id ? { ...pos, link_group_id: null } : pos
          )
        })));
        break;
        
      case 'position_unlinked':
        // Retirer le link_group_id de la position
        setCurrentSession(prev => {
          if (!prev) return prev;
          const updatedPeriods = prev.periods.map(period => ({
            ...period,
            positions: period.positions?.map(pos =>
              pos.id === message.position_id ? { ...pos, link_group_id: null } : pos
            )
          }));
          return { ...prev, periods: updatedPeriods };
        });
        setPeriods(prev => prev.map(period => ({
          ...period,
          positions: period.positions?.map(pos =>
            pos.id === message.position_id ? { ...pos, link_group_id: null } : pos
          )
        })));
        break;
        
      case 'stunt_created':
        setStunts(prev => [...prev, message.stunt]);
        setCurrentSession(prev => prev ? { ...prev, stunts: [...(prev.stunts || []), message.stunt] } : prev);
        break;
        
      case 'stunt_updated':
        setStunts(prev => prev.map(s => s.id === message.stunt.id ? message.stunt : s));
        setCurrentSession(prev => prev ? { ...prev, stunts: (prev.stunts || []).map(s => s.id === message.stunt.id ? message.stunt : s) } : prev);
        break;
        
      case 'stunt_deleted':
        setStunts(prev => prev.filter(s => s.id !== message.stunt_id));
        setCurrentSession(prev => prev ? { ...prev, stunts: (prev.stunts || []).filter(s => s.id !== message.stunt_id) } : prev);
        break;
        
      case 'stunt_group_created':
        setCurrentSession(prev => prev ? {
          ...prev,
          stunt_groups: [...(prev.stunt_groups || []), message.stunt_group]
        } : prev);
        break;
        
      case 'stunt_group_updated':
        setCurrentSession(prev => prev ? {
          ...prev,
          stunt_groups: (prev.stunt_groups || []).map(g =>
            g.id === message.stunt_group.id ? message.stunt_group : g
          )
        } : prev);
        break;
        
      case 'stunt_group_deleted':
        setCurrentSession(prev => prev ? {
          ...prev,
          stunt_groups: (prev.stunt_groups || []).filter(g => g.id !== message.group_id)
        } : prev);
        break;
        
      case 'stunt_group_dancers_added':
      case 'stunt_group_stunts_added':
        // Mettre à jour le stunt_group avec les nouveaux dancers/stunts
        setCurrentSession(prev => prev ? {
          ...prev,
          stunt_groups: (prev.stunt_groups || []).map(g =>
            g.id === message.stunt_group.id ? message.stunt_group : g
          )
        } : prev);
        break;
        
      default:
        console.log('Type de message non géré:', message.type);
    }
  };

  const { isConnected: wsConnected, send: wsSend, connectionCount } = useWebSocket(
    sessionId,
    user?.access_token,
    handleWebSocketMessage
  );

  // ✅ Tracking du curseur local pour collaboration temps réel
  const cursorThrottleRef = useRef(0);
  const CURSOR_THROTTLE_MS = 50; // Envoyer max toutes les 50ms
  
  const currentPeriod = periods[currentPeriodIndex];
  const currentPeriodId = currentPeriod?.id;

  // ✅ Charger les invitations quand le modal s'ouvre
  useEffect(() => {
    if (showShareModal && user?.access_token) {
      fetchSentInvitations();
    }
  }, [showShareModal]);

  // ✅ Mettre à jour la ref pour le callback WebSocket
  useEffect(() => {
    currentPeriodIdRef.current = currentPeriodId;
  }, [currentPeriodId]);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!wsConnected || !canvasRef.current || !currentPeriodId) return;
    // Ne pas envoyer si en mode trajectoire
    if (viewMode !== 'scene') return;

    const now = Date.now();
    if (now - cursorThrottleRef.current < CURSOR_THROTTLE_MS) return;
    cursorThrottleRef.current = now;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculer la position relative au canvas (normalisée 0-1)
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    // Ne pas envoyer si hors du canvas
    if (x < 0 || x > 1 || y < 0 || y > 1) return;

    wsSend({
      type: 'cursor_move',
      x: Math.round(x * 1000) / 1000, // Arrondir pour réduire la taille
      y: Math.round(y * 1000) / 1000,
      period_id: currentPeriodId,
      view_mode: viewMode
    });
  }, [wsConnected, wsSend, currentPeriodId, viewMode]);

  // Calcul des dimensions du canvas
  const {
    canvasWidth,
    canvasHeight,
    canvasPixelWidth,
    canvasPixelHeight,
    containerWidth,
    containerHeight,
  } = calculateCanvasDimensions(
    windowSize,
    SIDEBAR_WIDTH,
    MIN_MARGIN,
    TOP_BAR_HEIGHT,
    BOTTOM_BAR_HEIGHT,
    CANVAS_RATIO
  );

  // ✅ Mettre à jour la position du canvas pour les curseurs distants
  useEffect(() => {
    const updateCanvasOffset = () => {
      if (canvasRef.current) {
        // Utiliser requestAnimationFrame pour s'assurer que le layout est stable
        requestAnimationFrame(() => {
          if (canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            setCanvasOffset({ x: rect.left, y: rect.top });
            setCanvasRealSize({ width: rect.width, height: rect.height });
          }
        });
      }
    };
    
    // Délais multiples pour s'assurer que le canvas est bien rendu
    const timeouts = [
      setTimeout(updateCanvasOffset, 0),
      setTimeout(updateCanvasOffset, 100),
      setTimeout(updateCanvasOffset, 300),
      setTimeout(updateCanvasOffset, 500),
    ];
    
    updateCanvasOffset();
    window.addEventListener('resize', updateCanvasOffset);
    window.addEventListener('scroll', updateCanvasOffset);
    
    // Observer les changements de layout (par ex. sidebar qui s'ouvre/ferme)
    const resizeObserver = new ResizeObserver(updateCanvasOffset);
    if (canvasRef.current) {
      resizeObserver.observe(canvasRef.current);
      // Observer aussi le parent du canvas
      if (canvasRef.current.parentElement) {
        resizeObserver.observe(canvasRef.current.parentElement);
      }
    }
    
    // Observer les mutations du DOM qui pourraient affecter le layout
    const mutationObserver = new MutationObserver(() => {
      updateCanvasOffset();
    });
    if (canvasRef.current?.parentElement) {
      mutationObserver.observe(canvasRef.current.parentElement, {
        attributes: true,
        childList: true,
        subtree: false,
      });
    }
    
    return () => {
      timeouts.forEach(t => clearTimeout(t));
      window.removeEventListener('resize', updateCanvasOffset);
      window.removeEventListener('scroll', updateCanvasOffset);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [canvasWidth, canvasHeight, canvasPixelWidth, canvasPixelHeight, currentSession, viewMode]); // Recalculer aussi quand la session ou le mode de vue change

  // ✅ Nettoyage automatique des curseurs inactifs (timeout 5s)
  useEffect(() => {
    const CURSOR_TIMEOUT_MS = 5000; // 5 secondes d'inactivité
    
    const interval = setInterval(() => {
      const now = Date.now();
      setRemoteCursors(prev => {
        const next = { ...prev };
        let hasChanged = false;
        
        Object.entries(next).forEach(([userId, cursor]) => {
          if (now - cursor.timestamp > CURSOR_TIMEOUT_MS) {
            delete next[userId];
            hasChanged = true;
          }
        });
        
        return hasChanged ? next : prev;
      });
    }, 1000); // Vérifier toutes les secondes
    
    return () => clearInterval(interval);
  }, []);


  // -----------------------------
  // Raccourcis claviers (via hook)
  // -----------------------------
  useKeyboardShortcuts({
    dancers,
    selectedDancerId,
    setSelectedDancerId,
    setSelectedPositionId,
    periods,
    currentPeriodIndex,
    setCurrentPeriodIndex,
    audioRef,
    setCurrentTime,
    setIsPlaying,
    // ✅ Paramètres pour le mode link
    cursorMode,
    linkChain,
    handleCreateLinkGroup,
    setCursorMode,
    // ✅ Paramètres pour les menus contextuels
    contextMenuPosition,
    setContextMenuPosition,
    dancerContextMenu,
    setDancerContextMenu,
    // ✅ Paramètres pour le menu d'erreurs
    showErrorMenu,
    setShowErrorMenu,
  });


  // MEssages d'erreur

  useEffect(() => {
    const errors = [];
    if (currentSession && dancers.length > 0 && periods.length > 0) {
      periods.forEach((period) => {
        // Incohérence 1 : moins de positions que de danseurs
        if (period.positions && period.positions.length < dancers.length) {
          const diff = dancers.length - period.positions.length;
          errors.push({
            type: 'POSITIONS_MANQUANTES',
            periodName: period.name,
            message: `Période '${period.name}': positions manquantes : ${period.positions.length} position${period.positions.length > 1 ? 's' : ''} pour ${dancers.length} danseur${dancers.length > 1 ? 's' : ''}`
          });
        }

        // Incohérence 1b : plus de positions que de danseurs
        if (period.positions && period.positions.length > dancers.length) {
          const diff = period.positions.length - dancers.length;
          errors.push({
            type: 'POSITIONS_EN_TROP',
            periodName: period.name,
            message: `Période '${period.name}': positions en trop : ${period.positions.length} position${period.positions.length > 1 ? 's' : ''} pour ${dancers.length} danseur${dancers.length > 1 ? 's' : ''}`
          });
        }
  
        // Incohérence 2 : des skills requis non disponibles parmi les skills des danseurs
        const requiredSkillIds = new Set();
        period.positions?.forEach((pos) => {
          pos.skills?.forEach((skill) => requiredSkillIds.add(skill.id));
        });
        const availableSkillIds = new Set();
        dancers.forEach((dancer) => {
          dancer.skills?.forEach((skill) => availableSkillIds.add(skill.id));
        });
        requiredSkillIds.forEach((skillId) => {
          if (!availableSkillIds.has(skillId)) {
            const missingSkill =
              skills.find((s) => s.id === skillId)?.name || 'Skill inconnu';
            errors.push({
              type: 'SKILL_MANQUANT',
              periodName: period.name,
              message: `Période '${period.name}': le skill '${missingSkill}' requis n'est pas présent chez les danseurs`
            });
          }
        });

        // Incohérence 3 : Danseur assigné à plusieurs positions dans la même période
        const dancerAssignments = new Map();
        period.positions?.forEach((pos) => {
          if (pos.dancer_id) {
            if (dancerAssignments.has(pos.dancer_id)) {
              const dancer = dancers.find(d => d.id === pos.dancer_id);
              errors.push({
                type: 'DANSEUR_DUPLIQUE',
                periodName: period.name,
                message: `Période '${period.name}': le danseur '${dancer?.name || 'Inconnu'}' est assigné à plusieurs positions`
              });
            } else {
              dancerAssignments.set(pos.dancer_id, pos.id);
            }
          }
        });

        // Incohérence 4 : Skill requis à N positions mais seulement X<N danseurs ont ce skill
        const skillRequirementCount = new Map(); // skillId -> count
        period.positions?.forEach((pos) => {
          pos.skills?.forEach((skill) => {
            skillRequirementCount.set(skill.id, (skillRequirementCount.get(skill.id) || 0) + 1);
          });
        });
        
        skillRequirementCount.forEach((requiredCount, skillId) => {
          const dancersWithSkill = dancers.filter(d => 
            d.skills?.some(s => s.id === skillId)
          ).length;
          
          if (dancersWithSkill < requiredCount) {
            const skillName = skills.find(s => s.id === skillId)?.name || 'Skill inconnu';
            const diff = requiredCount - dancersWithSkill;
            errors.push({
              type: 'SKILL_INSUFFISANT',
              periodName: period.name,
              message: `Période '${period.name}': le skill '${skillName}' est requis ${requiredCount} fois mais seulement ${dancersWithSkill} danseur${dancersWithSkill > 1 ? 's' : ''} le possède${dancersWithSkill > 1 ? 'nt' : ''} (manque ${diff} danseur${diff > 1 ? 's' : ''})`
            });
          }
        });

        // Incohérence 5 : Position assignée à un danseur qui n'a pas tous les skills requis
        period.positions?.forEach((pos) => {
          if (pos.dancer_id && pos.skills && pos.skills.length > 0) {
            const assignedDancer = dancers.find(d => d.id === pos.dancer_id);
            if (assignedDancer) {
              const dancerSkillIds = new Set(assignedDancer.skills?.map(s => s.id) || []);
              const missingSkills = pos.skills.filter(s => !dancerSkillIds.has(s.id));
              
              if (missingSkills.length > 0) {
                const missingSkillNames = missingSkills.map(s => s.name).join(', ');
                errors.push({
                  type: 'SKILL_REQUIS_MANQUANT',
                  periodName: period.name,
                  message: `Période '${period.name}': le danseur '${assignedDancer.name}' n'a pas ${missingSkills.length > 1 ? 'les skills requis' : 'le skill requis'}: ${missingSkillNames}`
                });
              }
            }
          }
        });
      });

      // Incohérence 6 : Chevauchement de périodes (timestamps qui se chevauchent)
      const sortedPeriods = [...periods].sort((a, b) => (a.start_time || 0) - (b.start_time || 0));
      for (let i = 0; i < sortedPeriods.length - 1; i++) {
        const current = sortedPeriods[i];
        const next = sortedPeriods[i + 1];
        
        if (current.end_time !== null && next.start_time !== null && 
            current.end_time > next.start_time) {
          errors.push({
            type: 'CHEVAUCHEMENT',
            periodName: `${current.name}, ${next.name}`,
            message: `Chevauchement : la période '${current.name}' (fin: ${current.end_time.toFixed(2)}s) chevauche '${next.name}' (début: ${next.start_time.toFixed(2)}s)`
          });
        }
      }

      // ✅ Incohérence 7 : Positions liées (même link_group_id) avec des danseurs différents
      // Cette erreur n'est affichée que si le cadenas est fermé (forcedPosition = true)
      if (forcedPosition) {
        const linkGroups = new Map(); // link_group_id -> [positions]
        periods.forEach((period) => {
          period.positions?.forEach((pos) => {
            if (pos.link_group_id) {
              if (!linkGroups.has(pos.link_group_id)) {
                linkGroups.set(pos.link_group_id, []);
              }
              linkGroups.get(pos.link_group_id).push({
                ...pos,
                periodName: period.name
              });
            }
          });
        });

        linkGroups.forEach((positions, linkGroupId) => {
          // Vérifier que toutes les positions du groupe ont le même danseur
          const dancerIds = positions
            .filter(p => p.dancer_id !== null)
            .map(p => p.dancer_id);
          
          const uniqueDancerIds = new Set(dancerIds);
          
          if (uniqueDancerIds.size > 1) {
            // Plusieurs danseurs différents dans le même groupe
            const dancerNames = Array.from(uniqueDancerIds)
              .map(id => dancers.find(d => d.id === id)?.name || 'Inconnu')
              .join(', ');
            
            const periodNames = positions.map(p => p.periodName).join(', ');
            
            errors.push({
              type: 'CONFLIT_LIAISONS',
              periodName: periodNames,
              message: `Groupe de liaisons (périodes: ${periodNames}): des positions liées sont assignées à des danseurs différents (${dancerNames})`
            });
          }
        });
      }

      // ✅ Incohérence 8 : Position requiert plusieurs skills qu'aucun danseur ne possède simultanément
      periods.forEach((period) => {
        period.positions?.forEach((pos) => {
          if (pos.skills && pos.skills.length > 1) {
            // Cette position requiert plusieurs skills
            const requiredSkillIds = new Set(pos.skills.map(s => s.id));
            
            // Vérifier si au moins un danseur possède TOUS ces skills
            const hasDancerWithAllSkills = dancers.some(dancer => {
              const dancerSkillIds = new Set(dancer.skills?.map(s => s.id) || []);
              // Vérifier si tous les skills requis sont dans les skills du danseur
              return [...requiredSkillIds].every(skillId => dancerSkillIds.has(skillId));
            });
            
            if (!hasDancerWithAllSkills) {
              const skillNames = pos.skills.map(s => s.name).join(', ');
              errors.push({
                type: 'COMBINAISON_SKILLS_IMPOSSIBLE',
                periodName: period.name,
                message: `Période '${period.name}': une position requiert les skills [${skillNames}], mais aucun danseur ne possède tous ces skills simultanément`
              });
            }
          }
        });
      });

      // ✅ Incohérence 9 : Positions liées avec skills incompatibles
      const linkGroups = new Map(); // link_group_id -> [positions avec leur période]
      periods.forEach((period) => {
        period.positions?.forEach((pos) => {
          if (pos.link_group_id) {
            if (!linkGroups.has(pos.link_group_id)) {
              linkGroups.set(pos.link_group_id, []);
            }
            linkGroups.get(pos.link_group_id).push({
              position: pos,
              periodName: period.name
            });
          }
        });
      });

      linkGroups.forEach((linkedPositions, linkGroupId) => {
        // Collecter tous les skills requis dans ce groupe de liens
        const allRequiredSkills = new Set();
        const periodNames = [];
        
        linkedPositions.forEach(({ position, periodName }) => {
          periodNames.push(periodName);
          position.skills?.forEach(skill => allRequiredSkills.add(skill.id));
        });
        
        // Vérifier qu'au moins un danseur possède TOUS ces skills
        if (allRequiredSkills.size > 0) {
          const hasCompatibleDancer = dancers.some(dancer => {
            const dancerSkillIds = new Set(dancer.skills?.map(s => s.id) || []);
            return [...allRequiredSkills].every(skillId => dancerSkillIds.has(skillId));
          });
          
          if (!hasCompatibleDancer) {
            const skillNames = [...allRequiredSkills].map(skillId => 
              skills.find(s => s.id === skillId)?.name || 'Inconnu'
            ).join(', ');
            
            errors.push({
              type: 'POSITIONS_LIEES_INCOMPATIBLES',
              periodName: periodNames.join(', '),
              message: `Positions liées (${periodNames.join(', ')}): ces positions requièrent collectivement les skills [${skillNames}], mais aucun danseur ne possède tous ces skills simultanément. Les positions liées doivent être assignées au même danseur.`
            });
          }
        }
      });

      // ✅ Incohérence 10 : Instance de stunt avec plus de positions que de danseurs dans un groupe
      // On vérifie PAR INSTANCE, pas par total de positions
      if (currentSession.stunt_groups) {
        periods.forEach((period) => {
          // Grouper les positions par stunt_instance_id
          const instancesMap = new Map(); // stunt_instance_id -> { stuntId, positions: [] }
          
          period.positions?.forEach((pos) => {
            if (pos.stunt_instance_id && pos.stunts?.length > 0) {
              const stuntId = pos.stunts[0].id; // Le premier stunt de la position
              if (!instancesMap.has(pos.stunt_instance_id)) {
                instancesMap.set(pos.stunt_instance_id, { stuntId, positions: [] });
              }
              instancesMap.get(pos.stunt_instance_id).positions.push(pos);
            }
          });
          
          // Vérifier chaque instance individuellement
          instancesMap.forEach(({ stuntId, positions: instancePositions }, instanceId) => {
            const stunt = stunts.find(s => s.id === stuntId);
            const instanceSize = instancePositions.length;
            
            // Trouver TOUS les stunt groups qui contiennent ce stunt
            const stuntGroups = currentSession.stunt_groups?.filter(group =>
              group.stunts?.some(s => s.id === stuntId)
            ) || [];
            
            if (stuntGroups.length === 0) {
              // Pas de groupe pour ce stunt
              errors.push({
                type: 'STUNT_SANS_GROUPE',
                periodName: period.name,
                message: `Période '${period.name}': le stunt '${stunt?.name || 'Inconnu'}' est requis mais n'est assigné à aucun groupe`
              });
            } else {
              // Vérifier qu'au moins un groupe a assez de danseurs pour cette instance
              const hasValidGroup = stuntGroups.some(group => 
                (group.dancers?.length || 0) >= instanceSize
              );
              
              if (!hasValidGroup) {
                const maxGroupSize = Math.max(...stuntGroups.map(g => g.dancers?.length || 0));
                const diff = instanceSize - maxGroupSize;
                const groupsInfo = stuntGroups.map(g => `${g.name} (${g.dancers?.length || 0})`).join(', ');
                errors.push({
                  type: 'STUNT_INSUFFISANT',
                  periodName: period.name,
                  message: `Période '${period.name}': une instance du stunt '${stunt?.name || 'Inconnu'}' nécessite ${instanceSize} danseurs mais aucun groupe n'est assez grand. Groupes: ${groupsInfo}`
                });
              }
            }
          });
          
          // Vérifier aussi les positions avec stunt mais SANS instance_id (positions individuelles)
          period.positions?.forEach((pos) => {
            if (!pos.stunt_instance_id && pos.stunts?.length > 0) {
              pos.stunts.forEach((stunt) => {
                const stuntGroups = currentSession.stunt_groups?.filter(group =>
                  group.stunts?.some(s => s.id === stunt.id)
                ) || [];
                
                if (stuntGroups.length === 0) {
                  errors.push({
                    type: 'STUNT_SANS_GROUPE',
                    periodName: period.name,
                    message: `Période '${period.name}': le stunt '${stunt?.name || 'Inconnu'}' est requis mais n'est assigné à aucun groupe`
                  });
                }
              });
            }
          });
        });
      }

      // ✅ Incohérence 11 : Position assignée à un danseur qui n'est pas dans un stunt group requis
      if (currentSession.stunt_groups) {
        periods.forEach((period) => {
          period.positions?.forEach((pos) => {
            if (pos.dancer_id && pos.stunts && pos.stunts.length > 0) {
              const assignedDancer = dancers.find(d => d.id === pos.dancer_id);
              if (assignedDancer) {
                pos.stunts.forEach((stunt) => {
                  // ✅ Trouver tous les stunt groups qui contiennent ce stunt
                  const stuntGroups = currentSession.stunt_groups?.filter(group =>
                    group.stunts?.some(s => s.id === stunt.id)
                  );
                  
                  if (stuntGroups && stuntGroups.length > 0) {
                    // ✅ Vérifier que le danseur est dans au moins un des groupes
                    const dancerInAtLeastOneGroup = stuntGroups.some(group =>
                      group.dancers?.some(d => d.id === pos.dancer_id)
                    );
                    
                    if (!dancerInAtLeastOneGroup) {
                      errors.push({
                        type: 'STUNT_REQUIS_MANQUANT',
                        periodName: period.name,
                        message: `Période '${period.name}': le danseur '${assignedDancer.name}' n'est pas dans un groupe du stunt '${stunt.name}' requis pour cette position`
                      });
                    }
                  }
                });
              }
            }
          });
        });
      }
    }
    setSessionErrors(errors);
  }, [currentSession, periods, dancers, skills, stunts, forcedPosition]);



  // -----------------------------
  // 1) Charger la session
  // -----------------------------
  useEffect(() => {
    if (!sessionId || !user?.access_token) return;

    const fetchSession = async () => {
      try {
        const sessionData = await sessionApi.fetchSession(sessionId, user.access_token);
    
        // Tri par start_time pour l'ordre chronologique
        const sortedPeriods = (sessionData.periods || []).slice().sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
    
        setCurrentSession({
          ...sessionData,
          periods: sortedPeriods,
          stunt_groups: sessionData.stunt_groups || [], // ✅ Garder explicitement stunt_groups
        });
        setDancers(sessionData.dancers || []);
        setPeriods(sortedPeriods);
        setSkills(sessionData.skills || []);
        setStunts(sessionData.stunts || []);
    
        if (sortedPeriods.length > 0) {
          setCurrentPeriodIndex(0);
          // Positionner le timestamp au début de la première période
          if (sortedPeriods[0].start_time !== undefined && sortedPeriods[0].start_time !== null) {
            setCurrentTime(sortedPeriods[0].start_time);
          }
        }

        // Trouver la première couleur disponible pas encore utilisée pour un skill
        const usedColors = new Set(sessionData.skills?.map(skill => skill.color));
        const firstAvailableColor = skillColors.find(color => !usedColors.has(color)) || skillColors[0];

        setNewSkillColor(firstAvailableColor);
        setColorIndex(skillColors.indexOf(firstAvailableColor)); // Met à jour l'index

        // ✅ Vérification initiale : afficher en grisé si on est avant la première période au chargement
        // Note : currentTime est maintenant initialisé au début de la première période s'il existe
        setTimeout(() => {
          if (sortedPeriods.length > 0 && canvasRef.current) {
            const firstPeriod = sortedPeriods[0];
            const initialTime = firstPeriod.start_time !== undefined && firstPeriod.start_time !== null 
              ? firstPeriod.start_time 
              : 0;
            
            // Si le temps initial est avant la première période, afficher l'interpolation
            if (initialTime < firstPeriod.start_time) {
              afficherInterpolationPositionsUtil({
                canvasRef,
                currentSession: { ...sessionData, periods: sortedPeriods },
                periods: sortedPeriods,
                currentTime: initialTime,
                canvasPixelWidth: canvasRef.current.width,
                canvasPixelHeight: canvasRef.current.height,
                selectedPositionId: null,
                dancers: sessionData.dancers || [],
              });
            }
          }
        }, 150);

      } catch (error) {
        console.error('Erreur lors du chargement de la session :', error);
      }
    };

    fetchSession();
  }, [sessionId, user]);

  // -----------------------------
  // 2) Redessiner selon changements
  // -----------------------------
  useEffect(() => {
    if (viewMode === 'scene') {
      // Vérifier si on est hors période et en pause
      const currentPeriod = periods.find(
        (period) =>
          currentTime >= period.start_time - 0.01 && 
          currentTime <= period.end_time + 0.01
      );
      const inAPeriod = Boolean(currentPeriod);
      
      // Si on est hors période et en pause, afficher l'interpolation
      if (!inAPeriod && !isPlaying) {
        afficherInterpolationPositions();
      } else {
        drawScene();
      }
    } else {
      drawTrajectory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSession,
    currentPeriodIndex,
    selectedPositionId,
    selectedDancerId,
    viewMode,
    canvasPixelWidth,
    canvasPixelHeight,
    linkChain, // ✅ Ajouter linkChain pour redessiner quand la sélection change
  ]);


// fonctioon pour stopper l'animation quand on clique qque part sur la timeline 
  function stopAnimation(newTime) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
    pausedTime.current = null; // ✅ Réinitialiser car ce n'est plus une "pause durant animation"
    console.log("Animation stoppée par clic sur la timeline");
    setIsPlaying(false);

    // Vérifier si newTime est dans une période
    const currentPeriod = periods.find(
      period =>
        newTime >= period.start_time - 0.01 &&
        newTime < period.end_time + 0.01
    );

    // ✅ Mettre à jour currentPeriodIndex AVANT de dessiner pour éviter le flash
    if (currentPeriod) {
      const newPeriodIndex = periods.findIndex(p => p.id === currentPeriod.id);
      if (newPeriodIndex !== -1 && newPeriodIndex !== currentPeriodIndex) {
        setCurrentPeriodIndex(newPeriodIndex);
      }
      // drawScene() sera appelé par le useEffect qui surveille currentPeriodIndex
      return;
    }

    // Si le clic se fait entre deux périodes, afficher l'interpolation ou lancer une animation
    if (!isPlaying) {
      afficherInterpolationPositions();
    } else {
      lancerAnimationVersProchainePeriode();
    }
  }

  // ✅ Fonction helper pour les sauts de temps manuels (boutons, clics sur périodes, etc.)
  function jumpToTimeAndPause(newTime) {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
    setIsPlaying(false);
    pausedTime.current = null; // ✅ Réinitialiser car c'est un saut manuel, pas une pause durant animation
  }

  // ANIMATION


  useEffect(() => {
    if (!periods || periods.length === 0) return;
  
    // Détermine si currentTime se trouve dans l'une des périodes
    const currentPeriod = periods.find(
      (period) =>
        // On ajoute une marge de 10ms pour éviter les erreurs d'arrondi
        currentTime >= period.start_time-0.01 && currentTime < period.end_time + 0.01
    );
    const inAPeriod = Boolean(currentPeriod);
  
    // ✅ Gestion de la pause : capturer le temps UNIQUEMENT si on fait pause pendant une animation
    if (prevIsPlaying.current && !isPlaying) {
      // Passage de play à pause
      // On capture le temps SEULEMENT si une animation était en cours
      if (animationFrameRef.current) {
        pausedTime.current = currentTime;
        console.log('[PAUSE DURANT ANIMATION] Capture du temps:', pausedTime.current);
      } else {
        // Pause normale (pas durant une animation), on réinitialise
        pausedTime.current = null;
      }
    } else if (!prevIsPlaying.current && isPlaying) {
      // Passage de pause à play
      pausedTime.current = null;
    }

    // Ignorer les événements timeupdate résiduels UNIQUEMENT si on a fait pause durant une animation
    if (!isPlaying && pausedTime.current !== null && Math.abs(currentTime - pausedTime.current) > 0.01) {
      // C'est un événement résiduel après une pause durant animation, on l'ignore
      console.log('[PAUSE DURANT ANIMATION] Événement timeupdate résiduel ignoré');
      return;
    }
  
    // Si on est hors période et en mode PAUSE, afficher l'interpolation
    // (sauf si une animation est en cours OU si on vient de faire pause durant une animation OU si on est en train de mettre à jour une période)
    if (!inAPeriod && !isPlaying && !animationFrameRef.current && pausedTime.current === null && !isUpdatingPeriod.current) {
      afficherInterpolationPositions();
    }
  
    // ✅ Détection anticipée : lancer l'animation AVANT la sortie de période (proactive)
    if (inAPeriod && isPlaying && currentPeriod.end_time) {
      const timeUntilEnd = currentPeriod.end_time - currentTime;
      // Si on est à moins de 100ms de la fin, lancer l'animation maintenant
      if (timeUntilEnd <= 0.1 && timeUntilEnd > -0.01) {
        // Vérifier qu'il y a une période suivante
        const currentIndex = periods.findIndex(p => p.id === currentPeriod.id);
        const nextPeriod = periods[currentIndex + 1];
        if (nextPeriod && !animationFrameRef.current) {
          console.log(`[PROACTIVE] 🚀 Lancement anticipé de l'animation (${(timeUntilEnd * 1000).toFixed(0)}ms avant la fin)`);
          lancerAnimationVersProchainePeriode();
        }
      }
    }

    // ✅ Détection du passage de pause à play hors période
    if (!prevIsPlaying.current && isPlaying && !inAPeriod) {
      lancerAnimationVersProchainePeriode();
    }
    prevIsPlaying.current = isPlaying;

    // Détection de transition de l'état précédent
    if (prevInPeriod.current !== inAPeriod) {
      if (prevInPeriod.current && !inAPeriod && isPlaying) {
        console.log("Transition: sortie de période détectée.");
        // Ne lancer l'animation que si elle n'est pas déjà lancée
        if (!animationFrameRef.current) {
          lancerAnimationVersProchainePeriode();
        }
      } else if (!prevInPeriod.current && inAPeriod) {

        // Lors du passage de hors période à dans période, on dessine la scène complète
        drawScene();
      }
      // Mémoriser l'état courant pour la prochaine comparaison
      prevInPeriod.current = inAPeriod;
    }
  }, [currentTime, periods, isPlaying]);
  
  

  // Gestion de l'animation
const animationFrameRef = useRef(null);
const isPlayingRef = useRef(isPlaying);
useEffect(() => {
  isPlayingRef.current = isPlaying;
  // Si on passe en pause et qu'une animation est en cours, on l'annule et on redessine la scène.
  if (!isPlaying && animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
    //drawScene();
  }
}, [isPlaying]);

  // ✅ Nettoyer la linkChain quand on quitte le mode link
  useEffect(() => {
    if (cursorMode !== 'link') {
      clearLinkChain();
    }
  }, [cursorMode, clearLinkChain]);

  // -----------------------------
  // Animation vers prochaine période (wrapper)
  // -----------------------------
  function lancerAnimationVersProchainePeriode() {
    lancerAnimationVersProchainePeriodeUtil({
      canvasRef,
      currentSession,
      periods,
      currentTime,
      canvasPixelWidth,
      canvasPixelHeight,
      isPlayingRef,
      animationFrameRef,
      audioRef,
      setCurrentTime,
      dancers,
    });
  }

  // -----------------------------
  // 6) Interpolation des positions (wrapper)
  // -----------------------------
  function afficherInterpolationPositions() {
    afficherInterpolationPositionsUtil({
      canvasRef,
      currentSession,
      periods,
      currentTime,
      canvasPixelWidth,
      canvasPixelHeight,
      selectedPositionId,
      dancers,
    });
  }


  // -----------------------------
  // 3) Fonctions de dessin - SCENE (wrappers)
  // -----------------------------
  const drawScene = () => {
    drawSceneUtil({
      canvasRef,
      currentSession,
      currentPeriodIndex,
      canvasPixelWidth,
      canvasPixelHeight,
      periods,
      selectedPositionId,
      dancers,
      linkChain, // ✅ Passer linkChain pour afficher les positions en vert
    });
  };

  // -----------------------------
  // 4) Vue "Trajectory" (wrapper)
  // -----------------------------
  const drawTrajectory = () => {
    drawTrajectoryUtil({
      canvasRef,
      currentSession,
      selectedDancerId,
      setSelectedDancerId,
      dancers,
      periods,
      canvasPixelWidth,
      canvasPixelHeight,
    });
  };

  // -----------------------------
  // 5) Events souris - "scene" mode
  // -----------------------------
  const onCanvasMouseDown = (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    if (e.button !== 0) return; // clic gauche uniquement
    if (contextMenuPosition?.visible) return; // si menu contextuel ouvert, on ignore

    // ✅ Bloquer toutes les interactions si en mode PLAY
    if (isPlaying) {
      const actionMessages = {
        'circle': 'ajouter une position',
        'star': 'assigner un skill',
        'trash': 'supprimer une position',
        'link': 'gérer les liens',
        'default': 'modifier la scène',
      };
      const action = actionMessages[cursorMode] || 'effectuer cette action';
      showLinkError(`Veuillez mettre en PAUSE pour ${action}.`, 3000);
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Si on est en mode "circle", on ajoute directement une position
    if (cursorMode === 'circle') {
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
      addPositionAt(normX, normY);
      return; 
    }

    // Si on est en mode "star", on tente d'assigner un skill à la position cliquée
    if (cursorMode === 'star') {
      // On retrouve la position existante la plus proche (si < 8px)
      const period = currentSession.periods[currentPeriodIndex];
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      const clickedPos = period.positions.find((pos) => {
        const dx = pos.x - normX;
        const dy = pos.y - normY;
        return Math.sqrt(dx * dx + dy * dy) < 8;
      });

      if (clickedPos && selectedSkillId) {
        // Associe le skill sélectionné à la position
        associateSkillToPosition(clickedPos.id, selectedSkillId);
      }
      return;
    }

    // Si on est en mode "trash", on supprime la position cliquée
    if (cursorMode === 'trash') {
      const period = currentSession.periods[currentPeriodIndex];
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      // Recherche la position la plus proche dans un rayon de 8px
      const clickedPos = period.positions.find((pos) => {
        const dx = pos.x - normX;
        const dy = pos.y - normY;
        return Math.sqrt(dx * dx + dy * dy) < 8;
      });

      if (clickedPos) {
        // Appel de la fonction pour supprimer la position via l'API
        deletePosition(clickedPos.id);
      }
      return;
    }

    // ✅ Si on est en mode "link", on ajoute/retire la position de la chaîne
    if (cursorMode === 'link') {
      // ✅ Vérifier que currentTime est bien dans une période
      const currentPeriod = periods.find(
        (period) =>
          currentTime >= period.start_time - 0.01 && 
          currentTime <= period.end_time + 0.01
      );

      if (!currentPeriod) {
        // On n'est pas dans une période (on est dans une interpolation)
        showLinkError('Impossible de lier des positions affichées en cours de déplacement. Positionnez-vous exactement sur une période pour lier des positions.', 5000);
        return;
      }

      const period = currentSession.periods[currentPeriodIndex];
      const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      // Recherche la position la plus proche dans un rayon de 8px
      const clickedPos = period.positions.find((pos) => {
        const dx = pos.x - normX;
        const dy = pos.y - normY;
        return Math.sqrt(dx * dx + dy * dy) < 8;
      });

      if (clickedPos) {
        // ✅ Vérifier qu'on ne lie pas des positions de la même période
        const periodIdsInChain = linkChain.map(p => p.period_id);
        
        if (periodIdsInChain.includes(clickedPos.period_id) && !isPositionInChain(clickedPos.id)) {
          // On essaie d'ajouter une position d'une période déjà présente dans la chaîne
          showLinkError('Impossible de lier plusieurs positions de la même période.', 3000);
          return;
        }

        // Toggle la position dans la chaîne en cours d'édition
        togglePositionInChain(clickedPos);
        // Le redraw se fera automatiquement via le useEffect qui écoute linkChain
      }
      return;
    }

    // --- Sinon : comportement d’origine ("default")
    const period = currentSession.periods?.[currentPeriodIndex];
    if (!period) return;

    // Chercher la position proche
    const normX = (clickX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const normY = (clickY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
    const clickedPos = period.positions.find((pos) => {
      const dx = pos.x - normX;
      const dy = pos.y - normY;
      return Math.sqrt(dx * dx + dy * dy) < 8;
    });

    setPendingClickInfo({
      x: clickX,
      y: clickY,
      clickedPosId: clickedPos?.id || null,
      wasSelectedBefore: clickedPos?.id === selectedPositionId,
      isPotentialDragForSelected: !clickedPos && !!selectedPositionId,
    });

    setIsDragging(false);
    setDraggedPosId(null);
  };

  
  const onCanvasMouseMove = (e) => {
    // ✅ Envoyer la position du curseur pour la collaboration temps réel
    handleCanvasMouseMove(e);
    
    if (viewMode !== 'scene' || !currentSession) return;
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
  
    const moveX = e.clientX - rect.left;
    const moveY = e.clientY - rect.top;

    // Gestion du tooltip (fonctionne même en mode link/star/circle)
    const currentPeriod = currentSession.periods?.[currentPeriodIndex];
    if (currentPeriod) {
      const hoveredElement = detectHoveredElement(
        moveX,
        moveY,
        currentPeriod.positions,
        canvasPixelWidth,
        canvasPixelHeight,
        dancers,
        currentSession
      );

      if (hoveredElement) {
        // Annuler le timeout précédent s'il existe
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
        
        // Afficher le tooltip après un délai de 500ms
        tooltipTimeoutRef.current = setTimeout(() => {
          setTooltip({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            text: hoveredElement.text,
            position: hoveredElement.position,
          });
        }, 500);
      } else {
        // Annuler le timeout si on quitte la zone
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
          tooltipTimeoutRef.current = null;
        }
        setTooltip({ visible: false, x: 0, y: 0, text: '' });
      }
    }

    if (cursorMode !== 'default') return;
    if (!pendingClickInfo) return;
    
    // ✅ Bloquer le drag si en mode PLAY
    if (isPlaying) return;
  
    if (isDragging && draggedPosId) {
      // Mise à jour de la position pendant le drag
      const dx = moveX - dragStart.x;
      const dy = moveY - dragStart.y;
      const updatedSession = { ...currentSession };
      const period = updatedSession.periods[currentPeriodIndex];
      const posIndex = period.positions.findIndex((p) => p.id === draggedPosId);
  
      if (posIndex !== -1) {
        const normDX = (dx / canvasPixelWidth) * CANVAS_BASE_WIDTH;
        const normDY = (dy / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
        period.positions[posIndex].x += normDX;
        period.positions[posIndex].y += normDY;
        setCurrentSession(updatedSession);
      }
      setDragStart({ x: moveX, y: moveY });
      return;
    }
  
    const dist = Math.hypot(moveX - pendingClickInfo.x, moveY - pendingClickInfo.y);
    if (dist > THRESHOLD) {
      // Ne prendre en compte que le clic sur une position (clickedPosId)
      const posIdToDrag = pendingClickInfo.clickedPosId;
      if (posIdToDrag) {
        setIsDragging(true);
        setDraggedPosId(posIdToDrag);
        setDragStart({ x: moveX, y: moveY });
      }
    }
  };
  
  const onCanvasMouseUp = async (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    if (!pendingClickInfo) return;
    
    // ✅ Bloquer le drop si en mode PLAY
    if (isPlaying) {
      setPendingClickInfo(null);
      return;
    }
    
    // Idem : on ne gère le "drop" que si on est en mode default
    if (cursorMode !== 'default') {
      setPendingClickInfo(null);
      return;
    }

    if (isDragging && draggedPosId) {
      // Fin d'un drag
      setIsDragging(false);

      const rect = canvasRef.current.getBoundingClientRect();
      const upX = e.clientX - rect.left;
      const upY = e.clientY - rect.top;

      // Snap to grid
      const cellSize = canvasPixelWidth / 28;
      const snapX = Math.round(upX / cellSize) * cellSize;
      const snapY = Math.round(upY / cellSize) * cellSize;

      const normalizedX = (snapX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
      const normalizedY = (snapY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

      try {
        // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
        await wsAction.updatePosition(
          wsSend,
          {
            position_id: draggedPosId,
            x: normalizedX,
            y: normalizedY
          },
          user.access_token
        );
      } catch (error) {
        console.error('Error updating position:', error);
      }

      setDraggedPosId(null);
    } else {
      // Simple clic
      const period = currentSession.periods[currentPeriodIndex];
      const { clickedPosId, wasSelectedBefore } = pendingClickInfo;

      if (clickedPosId) {
        if (wasSelectedBefore) {
          handlePositionSelect(null, null);
        } else {
          const pos = period.positions.find((p) => p.id === clickedPosId);
          handlePositionSelect(clickedPosId, pos?.dancer_id);
        }
      }
    }

    setPendingClickInfo(null);
  };

  const onCanvasContextMenu = (e) => {
    if (viewMode !== 'scene' || !currentSession) return;
    e.preventDefault();

    // ✅ Bloquer le menu contextuel si en mode PLAY
    if (isPlaying) {
      showLinkError('Veuillez mettre en PAUSE pour accéder au menu contextuel.', 3000);
      return;
    }
  
    // Utilisation des coordonnées de la fenêtre
    const clickX = e.pageX;
    const clickY = e.pageY;
  
    // Définir la largeur et la hauteur estimée du menu (à ajuster au besoin)
    const menuWidth = 200;
    const menuHeight = 220;
  
    // Limiter pour que le menu ne dépasse pas l'écran
    const limitedX = clickX + menuWidth > window.innerWidth
      ? window.innerWidth - menuWidth - 5
      : clickX;
    const limitedY = clickY + menuHeight > window.innerHeight
      ? window.innerHeight - menuHeight - 5
      : clickY;
  
    const period = currentSession.periods?.[currentPeriodIndex];
    if (!period) return;
  
    // Recherche de la position cliquée (reste identique)
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const normX = (offsetX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const normY = (offsetY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;
    const clickedPos = period.positions.find((pos) => {
      const dx = pos.x - normX;
      const dy = pos.y - normY;
      return Math.sqrt(dx * dx + dy * dy) < 8;
    });
  
    const options = [];
  
    if (!clickedPos) {
      // Vérifier s'il y a des danseurs associés dans la période
      const hasAssociatedDancers = period.positions.some(pos => pos.dancer_id !== null);
      
      options.push({
        label: 'Ajouter une position',
        onClick: () => addPositionAt(normX, normY),
      });
      
      // Ajouter l'option "Dissocier tous les danseurs" uniquement si nécessaire
      if (hasAssociatedDancers) {
        options.push({
          label: 'Dissocier tous les danseurs',
          onClick: () => dissociateAllDancers(),
        });
      }
    } else {
      if (clickedPos.dancer_id) {
        options.push(
          {
            label: 'Dissocier le danseur',
            onClick: () => dissociateDancerFromPosition(clickedPos.id),
          },
          {
            label: 'Supprimer cette position',
            onClick: () => deletePosition(clickedPos.id),
          }
        );
      } else {
        // ✅ Afficher uniquement les dancers non assignés sur cette période
        const associatedDancerIds = period.positions
          .map((p) => p.dancer_id)
          .filter(Boolean);
        const availableDancers = (currentSession?.dancers || []).filter(
          (d) => !associatedDancerIds.includes(d.id)
        );
        options.push(
          {
            label: 'Associer un danseur',
            subMenu: availableDancers.map((dancer) => ({
              label: dancer.name,
              onClick: () => associateDancerToPosition(clickedPos.id, dancer.id),
            })),
          },
          {
            label: 'Supprimer cette position',
            onClick: () => deletePosition(clickedPos.id),
          }
        );
      }
  
      const currentSkills = clickedPos.skills || [];
      const subMenuAssocierSkill = (currentSession?.skills || [])
        .filter((sk) => !currentSkills.some((posSk) => posSk.id === sk.id))
        .map((sk) => ({
          label: sk.name,
          onClick: () => associateSkillToPosition(clickedPos.id, sk.id),
        }));
  
      const subMenuDissocierSkill = currentSkills.map((sk) => ({
        label: sk.name,
        onClick: () => removeSkillFromPosition(clickedPos.id, sk.id),
      }));
  
      if (subMenuAssocierSkill.length > 0) {
        options.push({
          label: 'Associer un skill',
          subMenu: subMenuAssocierSkill,
        });
      }
      if (subMenuDissocierSkill.length > 0) {
        options.push({
          label: 'Dissocier un skill',
          subMenu: subMenuDissocierSkill,
        });
      }

      // Options pour les stunts
      const currentStunts = clickedPos.stunts || [];
      const subMenuAssocierStunt = [];
      
      for (const st of (currentSession?.stunts || [])) {
        // Si le stunt n'est pas encore associé à cette position
        if (!currentStunts.some((posSt) => posSt.id === st.id)) {
          // Vérifier s'il existe déjà des instances de ce stunt sur la période
          const instancesOnPeriod = getStuntInstancesOnPeriod(clickedPos.period_id, st.id);
          
          if (instancesOnPeriod.length > 0) {
            // Créer un sous-menu pour ce stunt avec les options d'instances
            console.log(`📋 Stunt "${st.name}" a ${instancesOnPeriod.length} instance(s) sur la période`);
            const instanceSubMenu = [
              ...instancesOnPeriod.map((instance, idx) => {
                const posId = clickedPos.id;
                const stuntId = st.id;
                const instanceId = instance.instance_id;
                const minPosId = Math.min(...instance.position_ids);
                console.log(`  - Instance ${idx + 1}: instance_id=${instanceId}, positions=${instance.position_ids.length}, min_pos_id=${minPosId}`);
                return {
                  label: `Ajouter à l'instance ${idx + 1} (${instance.position_ids.length} positions)`,
                  onClick: () => {
                    console.log(`🎬 Clic sur "Ajouter à l'instance ${idx + 1}"`);
                    addStuntToPosition(posId, stuntId, instanceId);
                  },
                };
              }),
              {
                label: 'Créer une nouvelle instance',
                onClick: () => {
                  console.log(`🎬 Clic sur "Créer une nouvelle instance"`);
                  addStuntToPosition(clickedPos.id, st.id, null, true);
                },
              }
            ];
            
            subMenuAssocierStunt.push({
              label: st.name,
              subMenu: instanceSubMenu,
            });
          } else {
            // Pas d'instance existante, créer directement
            subMenuAssocierStunt.push({
              label: st.name,
              onClick: () => addStuntToPosition(clickedPos.id, st.id, null, true),
            });
          }
        }
      }
  
      const subMenuDissocierStunt = currentStunts.map((st) => ({
        label: st.name,
        onClick: () => removeStuntFromPosition(clickedPos.id, st.id),
      }));
  
      if (subMenuAssocierStunt.length > 0) {
        options.push({
          label: 'Associer un stunt',
          subMenu: subMenuAssocierStunt,
        });
      }
      if (subMenuDissocierStunt.length > 0) {
        options.push({
          label: 'Dissocier un stunt',
          subMenu: subMenuDissocierStunt,
        });
      }

      // ✅ Options de liaison
      const hasLinkOptions = clickedPos.link_group_id || cursorMode !== 'link';
      
      if (hasLinkOptions) {
        // Ajouter un séparateur avant les options de liaison
        options.push({ type: 'separator' });
      }

      if (clickedPos.link_group_id) {
        // Position déjà liée
        const linkOptions = [];
        
        // "Lier à d'autres positions" seulement si on n'est pas déjà en mode link
        if (cursorMode !== 'link') {
          linkOptions.push({
            label: 'Lier à d\'autres positions',
            onClick: () => {
              // Récupérer toutes les positions du groupe
              const groupPositions = getPositionsByLinkGroup(clickedPos.link_group_id);
              // Passer en mode link
              setCursorMode('link');
              // Remplacer directement la chaîne avec les positions du groupe
              setLinkChainDirectly(groupPositions);
            },
          });
        }
        
        linkOptions.push(
          {
            label: 'Dé-lier la position',
            onClick: async () => {
              // Vérifier s'il n'y a que 2 positions dans le groupe
              const groupPositions = getPositionsByLinkGroup(clickedPos.link_group_id);
              if (groupPositions.length === 2) {
                // Supprimer tout le groupe pour éviter qu'une position reste seule
                await handleDeleteLinkGroup(clickedPos.link_group_id);
              } else {
                // Juste dé-lier cette position
                await handleUnlinkPosition(clickedPos.id);
              }
            },
          },
          {
            label: 'Supprimer la chaîne de liens',
            onClick: () => handleDeleteLinkGroup(clickedPos.link_group_id),
          }
        );
        
        options.push(...linkOptions);
      } else if (cursorMode !== 'link') {
        // Position non liée ET pas en mode link
        options.push({
          label: 'Lier à d\'autres positions',
          onClick: () => {
            setCursorMode('link');
            togglePositionInChain(clickedPos);
          },
        });
      }
    }
  
    // Ouvrir le menu contextuel avec useFixed: true pour que le menu puisse apparaître en dehors du canvas
    setContextMenuPosition({
      x: limitedX,
      y: limitedY,
      options,
      visible: true,
      useFixed: true,
    });
  };
  

  function handleDancerRightClick(dancer, e) {
    e.preventDefault();
    
    // ✅ Bloquer le menu contextuel si en mode PLAY
    if (isPlaying) {
      showLinkError('Veuillez mettre en PAUSE pour accéder au menu contextuel.', 3000);
      return;
    }
    
      // Construire les sous-menus
      const subMenuAssocier = (skills || [])
      // On ne propose que les skills qui ne sont pas déjà associés
      .filter((sk) => !dancer.skills.some(ds => ds.id === sk.id))
      // On construit les options
      .map((sk) => {
        return {
          label: sk.name,
          onClick: () => {
            // on ne fera rien si c'est disabled (on vérifie côté ContextMenu)
            associateSkillToDancer(dancer.id, sk.id);
          }
        }
      });
    
      const subMenuDissocier = (dancer.skills || []).map((sk) => ({
        label: sk.name,
        onClick: () => {
          dissociateSkillFromDancer(dancer.id, sk.id);
        },
      }));
    
      // Construction de la liste d'options
      const options = [
        {
          label: 'Supprimer le danseur',
          onClick: () => {
            deleteDancer(dancer.id);
          },
        },
        {
          label: 'Associer un skill',
          subMenu: subMenuAssocier, 
        },
      ];
    
      // On n'ajoute "Dissocier un skill" que si le danseur a des skills
      if (dancer.skills && dancer.skills.length > 0) {
        options.push({
          label: 'Dissocier un skill',
          subMenu: subMenuDissocier,
        });
      }
    
      // Calculer position X/Y
      const clickX = e.pageX;
      const clickY = e.pageY;
    
      // On peut éventuellement limiter l'emplacement pour ne pas dépasser l'écran
      // (comme on le fait dans onCanvasContextMenu). Ex:
      // const menuWidth = 200;
      // const limitedX = Math.min(clickX, windowSize.width - menuWidth);
      // const limitedY = Math.min(clickY, windowSize.height - 220);
    
      setDancerContextMenu({
        x: clickX,
        y: clickY,
        visible: true,
        dancerId: dancer.id,
        options,
        useFixed: true,  // <--- on utilise position: fixed
      });
    }

  // -----------------------------
  // Fonction pour détecter l'élément survolé
  // -----------------------------
  function detectHoveredElement(mouseX, mouseY, positions, canvasWidth, canvasHeight, dancers, session) {
    if (!positions || !Array.isArray(positions)) return null;
    
    const scaleX = canvasWidth / CANVAS_BASE_WIDTH;
    const scaleY = canvasHeight / CANVAS_BASE_HEIGHT;

    for (const pos of positions) {
      const scaledX = pos.x * scaleX;
      const scaledY = pos.y * scaleY;
      const r = 8;

      // 1) Vérifier le badge de lien (en haut à gauche de la position)
      if (pos.link_group_id) {
        const iconSize = 17;
        const badgeX = scaledX - r - iconSize / 2 - 7;
        const badgeY = scaledY - r - iconSize / 2 - 5;
        
        if (
          mouseX >= badgeX &&
          mouseX <= badgeX + iconSize &&
          mouseY >= badgeY &&
          mouseY <= badgeY + iconSize
        ) {
          // Compter le nombre de positions liées dans ce groupe
          let linkedCount = 0;
          session.periods?.forEach(period => {
            period.positions?.forEach(p => {
              if (p.link_group_id === pos.link_group_id) {
                linkedCount++;
              }
            });
          });
          return { text: `${linkedCount} position${linkedCount > 1 ? 's' : ''} liée${linkedCount > 1 ? 's' : ''}`, position: 'above' };
        }
      }

      // 2) Vérifier les skills (étoiles en haut à droite)
      if (pos.skills && pos.skills.length > 0) {
        const startX = scaledX + 7;
        const startY = scaledY - 7;
        
        for (let i = 0; i < pos.skills.length; i++) {
          const sk = pos.skills[i];
          const offset = i * 12;
          const starX = startX + offset;
          const starY = startY;
          
          // Zone de détection autour de l'étoile (environ 12px de large)
          if (
            mouseX >= starX - 2 &&
            mouseX <= starX + 10 &&
            mouseY >= starY - 10 &&
            mouseY <= starY + 2
          ) {
            return { text: `Skill : ${sk.name}`, position: 'above' };
          }
        }
      }

      // 3) Vérifier les stunts (cercles en bas à gauche)
      if (pos.stunts && pos.stunts.length > 0) {
        const startX = scaledX - 14;
        const startY = scaledY + 9;
        
        for (let i = 0; i < pos.stunts.length; i++) {
          const st = pos.stunts[i];
          const offset = i * 12;
          const circleX = startX - offset;
          const circleY = startY;
          
          // Zone de détection autour du cercle (rayon 4px + marge)
          const distance = Math.sqrt(
            Math.pow(mouseX - circleX, 2) + Math.pow(mouseY - circleY, 2)
          );
          
          if (distance <= 6) {
            // ✅ Afficher l'information d'instance si disponible
            let tooltipText = `Stunt : ${st.name}`;
            if (pos.stunt_instance_id) {
              // Compter le nombre de positions dans cette instance
              const currentPeriod = session.periods?.[currentPeriodIndex];
              if (currentPeriod) {
                const instancePositions = currentPeriod.positions?.filter(
                  p => p.stunt_instance_id === pos.stunt_instance_id && 
                       p.stunts?.some(s => s.id === st.id)
                ) || [];
                
                // Trouver l'index de l'instance (combien d'instances de ce stunt existent sur la période)
                const allInstances = {};
                currentPeriod.positions?.forEach(p => {
                  if (p.stunt_instance_id && p.stunts?.some(s => s.id === st.id)) {
                    if (!allInstances[p.stunt_instance_id]) {
                      allInstances[p.stunt_instance_id] = [];
                    }
                    allInstances[p.stunt_instance_id].push(p);
                  }
                });
                
                const instanceIds = Object.keys(allInstances).sort();
                const instanceNumber = instanceIds.indexOf(pos.stunt_instance_id) + 1;
                
                tooltipText += ` - Instance ${instanceNumber} (${instancePositions.length} position${instancePositions.length > 1 ? 's' : ''})`;
              }
            }
            return { text: tooltipText, position: 'below' };
          }
        }
      }
    }

    return null;
  }

  // -----------------------------
  // 6) Associer / Dissocier
  // -----------------------------
  async function associateDancerToPosition(positionId, dancerId) {
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.assignDancer(
        wsSend,
        {
          position_id: positionId,
          dancer_id: dancerId
        },
        user.access_token
      );
    } catch (error) {
      console.error('Erreur d\'association :', error);
    }
  }

  async function dissociateDancerFromPosition(positionId) {
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.assignDancer(
        wsSend,
        {
          position_id: positionId,
          dancer_id: null
        },
        user.access_token
      );
    } catch (error) {
      console.error('Erreur de dissociation :', error);
    }
  }

  async function dissociateAllDancers() {
    try {
      // ✅ Dissocier tous les dancers via WebSocket (appels individuels)
      const period = currentSession.periods[currentPeriodIndex];
      const positionsWithDancers = period.positions.filter(pos => pos.dancer_id !== null);
      
      // Envoyer tous les appels en parallèle
      await Promise.all(
        positionsWithDancers.map(pos =>
          wsAction.assignDancer(
            wsSend,
            {
              position_id: pos.id,
              dancer_id: null
            },
            user.access_token
          )
        )
      );
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
    } catch (error) {
      console.error('Erreur de dissociation globale :', error);
    }
  }

  async function associateSkillToPosition(posId, skillId) {
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.associateSkillToPosition(
        wsSend,
        {
          session_id: currentSession.id,
          position_id: posId,
          skill_id: skillId
        },
        user.access_token
      );
    } catch (err) {
      console.error("Erreur association skill sur position :", err);
    }
  }
  
  async function removeSkillFromPosition(posId, skillId) {
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.removeSkillFromPosition(
        wsSend,
        {
          session_id: currentSession.id,
          position_id: posId,
          skill_id: skillId
        },
        user.access_token
      );
    } catch (err) {
      console.error("Erreur dissociation skill sur position :", err);
    }
  }

  // -----------------------------
  // 6a-bis) Fonctions associer/dissocier stunts
  // -----------------------------
  
  /**
   * Récupère les instances existantes d'un stunt sur une période
   */
  function getStuntInstancesOnPeriod(periodId, stuntId) {
    const period = currentSession?.periods?.find(p => p.id === periodId);
    if (!period) return [];
    
    const instances = {};
    for (const pos of period.positions || []) {
      const hasStunt = (pos.stunts || []).some(s => s.id === stuntId);
      if (hasStunt && pos.stunt_instance_id) {
        if (!instances[pos.stunt_instance_id]) {
          instances[pos.stunt_instance_id] = {
            instance_id: pos.stunt_instance_id,
            position_ids: []
          };
        }
        instances[pos.stunt_instance_id].position_ids.push(pos.id);
      }
    }
    
    // Trier les instances par le timestamp dans l'instance_id (format: stunt_X_TIMESTAMP_random)
    // L'instance créée en premier doit apparaître en premier
    return Object.values(instances).sort((a, b) => {
      // Extraire le timestamp de l'instance_id (ex: "stunt_15_1764083239432_xyz" -> 1764083239432)
      const getTimestamp = (instanceId) => {
        const parts = instanceId.split('_');
        if (parts.length >= 3) {
          return parseInt(parts[2], 10) || 0;
        }
        return 0;
      };
      return getTimestamp(a.instance_id) - getTimestamp(b.instance_id);
    });
  }
  
  async function addStuntToPosition(posId, stuntId, stuntInstanceId = null, createNew = false) {
    console.log(`🎯 addStuntToPosition appelé:`, { posId, stuntId, stuntInstanceId, createNew });
    try {
      // Si createNew, générer un nouvel instance_id
      let instanceId = stuntInstanceId;
      if (createNew) {
        instanceId = `stunt_${stuntId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        console.log(`  📝 Création d'un nouvel instance_id: ${instanceId}`);
      } else if (stuntInstanceId) {
        console.log(`  🔗 Utilisation de l'instance existante: ${instanceId}`);
      }
      
      console.log(`  📤 Envoi de la requête WebSocket...`);
      await wsAction.associateStuntToPosition(
        wsSend,
        {
          session_id: currentSession.id,
          position_id: posId,
          stunt_id: stuntId,
          stunt_instance_id: instanceId
        },
        user.access_token
      );
      console.log(`  ✅ Stunt associé avec succès`);
    } catch (err) {
      console.error("❌ Erreur association stunt sur position :", err);
    }
  }
  
  async function removeStuntFromPosition(posId, stuntId) {
    try {
      await wsAction.removeStuntFromPosition(
        wsSend,
        {
          session_id: currentSession.id,
          position_id: posId,
          stunt_id: stuntId
        },
        user.access_token
      );
    } catch (err) {
      console.error("Erreur dissociation stunt sur position :", err);
    }
  }

  // -----------------------------
  // 6b) Fonctions de gestion des liaisons (Link Groups)
  // -----------------------------
  
  /**
   * Crée un groupe de liaisons avec les positions dans linkChain
   */
  async function handleCreateLinkGroup() {
    if (linkChain.length < 2) {
      alert('Vous devez sélectionner au moins 2 positions pour créer un lien.');
      return;
    }

    // Vérifier que les positions sont de périodes différentes
    const periodIds = linkChain.map(p => p.period_id);
    const uniquePeriodIds = new Set(periodIds);
    if (uniquePeriodIds.size !== linkChain.length) {
      alert('Impossible de lier des positions de la même période.');
      return;
    }

    try {
      const positionIds = linkChain.map(p => p.id);
      await wsAction.createLinkGroup(
        wsSend,
        {
          session_id: currentSession.id,
          position_ids: positionIds
        },
        user.access_token
      );
      
      // Réinitialiser la chaîne et revenir en mode default
      clearLinkChain();
      setCursorMode('default');
    } catch (error) {
      console.error('Erreur lors de la création du groupe de liaisons:', error);
      alert(error.message || 'Erreur lors de la création du groupe de liaisons');
    }
  }

  /**
   * Délie une position de son groupe
   */
  async function handleUnlinkPosition(positionId) {
    try {
      await wsAction.unlinkPosition(
        wsSend,
        {
          session_id: currentSession.id,
          position_id: positionId
        },
        user.access_token
      );
    } catch (error) {
      console.error('Erreur lors de la déliaison:', error);
      alert(error.message || 'Erreur lors de la déliaison de la position');
    }
  }

  /**
   * Supprime un groupe de liaisons entier
   */
  async function handleDeleteLinkGroup(linkGroupId) {
    if (!window.confirm('Voulez-vous vraiment supprimer les liens entre toutes les positions de cette chaîne ?')) {
      return;
    }

    try {
      await wsAction.deleteLinkGroup(
        wsSend,
        {
          session_id: currentSession.id,
          link_group_id: linkGroupId
        },
        user.access_token
      );
    } catch (error) {
      console.error('Erreur lors de la suppression du groupe:', error);
      alert(error.message || 'Erreur lors de la suppression du groupe de liaisons');
    }
  }

  // -----------------------------
  // 7) Sélections
  // -----------------------------

  function handlePositionSelect(posId, dancerId) {
    setSelectedPositionId(posId);
    setSelectedDancerId(dancerId || null);
  }

  function handleDancerSelect(dancerId) {
    if (cursorMode === 'star') {
      // Si une skill est sélectionnée => associer la skill au danseur
      if (selectedSkillId) {
        associateSkillToDancer(dancerId, selectedSkillId);
      }
      // Sinon, star "creuse" => pas d'action
      return;
    }

    // Sinon, mode normal => sélection
    setSelectedDancerId(dancerId);
    const period = currentSession?.periods?.[currentPeriodIndex];
    if (!period) return;
    const pos = period.positions.find((p) => p.dancer_id === dancerId);
    setSelectedPositionId(pos?.id || null);
  }

  useEffect(() => {
    if (selectedDancerId && periods.length > 0) {
      const newPeriod = periods[currentPeriodIndex];
      if (newPeriod) {
        const newPosition = newPeriod.positions.find(pos => pos.dancer_id === selectedDancerId);
        setSelectedPositionId(newPosition ? newPosition.id : null);
      }
    }
  }, [currentPeriodIndex, selectedDancerId, periods]);
  

  // -----------------------------
  // 8) Dancers & Skills
  // -----------------------------
  async function addDancer() {
    if (!newDancerName.trim() || !currentSession) return;
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.createDancer(
        wsSend,
        {
          session_id: currentSession.id,
          name: newDancerName
        },
        user.access_token
      );
      setNewDancerName('');
    } catch (err) {
      console.error('Erreur création danseur :', err);
    }
  }

  async function deleteDancer(dancerId) {
    if (!window.confirm('Supprimer ce danseur ?') || !currentSession) return;
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.deleteDancer(
        wsSend,
        { dancer_id: dancerId },
        user.access_token
      );
    } catch (err) {
      console.error('Erreur suppression danseur :', err);
    }
  }

  async function addSkill() {
    if (!newSkillName.trim() || !currentSession) return;
    
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.createSkill(
        wsSend,
        {
          session_id: currentSession.id,
          name: newSkillName,
          color: newSkillColor
        },
        user.access_token
      );
  
      setNewSkillName('');
  
      // Passer à la prochaine couleur de la liste si l'utilisateur n'a pas choisi manuellement
      const nextIndex = (colorIndex + 1) % skillColors.length;
      setColorIndex(nextIndex);
      setNewSkillColor(skillColors[nextIndex]); // Met à jour la couleur par défaut
    } catch (err) {
      console.error('Erreur création skill :', err);
    }
  }

  async function deleteSkill(skillId) {
    if (!window.confirm('Supprimer ce skill ?') || !currentSession) return;
    try {
        // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
        await wsAction.deleteSkill(
          wsSend,
          { skill_id: skillId },
          user.access_token
        );
        setSelectedSkillId(null); // ✅ Désélectionner le skill supprimé
    } catch (err) {
        console.error('Erreur suppression skill :', err);
    }
}


async function associateSkillToDancer(dancerId, skillId) {
  try {
    // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
    await wsAction.associateSkillToDancer(
      wsSend,
      {
        session_id: currentSession.id,
        dancer_id: dancerId,
        skill_id: skillId
      },
      user.access_token
    );
  } catch (err) {
    console.error("Erreur association skill:", err);
  }
}

async function dissociateSkillFromDancer(dancerId, skillId) {
  try {
    // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
    await wsAction.removeSkillFromDancer(
      wsSend,
      {
        session_id: currentSession.id,
        dancer_id: dancerId,
        skill_id: skillId
      },
      user.access_token
    );
  } catch (err) {
    console.error("Erreur dissociation skill:", err);
  }
}

  // -----------------------------
  // 9) Positions
  // -----------------------------
  async function deletePosition(positionId) {
    if (!currentSession) return;
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.deletePosition(
        wsSend,
        { position_id: positionId },
        user.access_token
      );
    } catch (error) {
      console.error('Error deleting position:', error);
    }
  }

  /**
   * Ajoute une position en (normX, normY) référentiel 0..1045/0..665
   * On applique ensuite un snap éventuel si besoin ou direct
   */
  async function addPositionAt(normX, normY) {
    if (!currentSession) return;
    const currentPeriod = currentSession.periods[currentPeriodIndex];
    if (!currentPeriod) return;

    // Snap: on convertit (normX, normY) en pixel, on snap, puis on re-normalise
    const pixelX = (normX / CANVAS_BASE_WIDTH) * canvasPixelWidth;
    const pixelY = (normY / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

    const cellSize = canvasPixelWidth / 28;
    const snapX = Math.round(pixelX / cellSize) * cellSize;
    const snapY = Math.round(pixelY / cellSize) * cellSize;

    const finalNormX = (snapX / canvasPixelWidth) * CANVAS_BASE_WIDTH;
    const finalNormY = (snapY / canvasPixelHeight) * CANVAS_BASE_HEIGHT;

    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.createPosition(
        wsSend,
        {
          period_id: currentPeriod.id,
          x: finalNormX,
          y: finalNormY
        },
        user.access_token
      );
    } catch (error) {
      console.error('Error creating position:', error);
    }
  }

  // -----------------------------
  // 10) Periods
  // -----------------------------
  
  // formater le timestamp affiché dans la barre latterale 
  function formatTimestamp(ts) {
    if (ts === null || ts === undefined) return '--:--';
    const minutes = Math.floor(ts / 60);
    const seconds = Math.floor(ts % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  async function addPeriod() {
    if (!newPeriodName.trim() || !currentSession) return;
    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      const newPeriod = await wsAction.createPeriod(
        wsSend,
        {
          session_id: currentSession.id,
          name: newPeriodName
        },
        user.access_token
      );
  
      // ✅ Sélectionner la nouvelle période (après que handleWebSocketMessage l'ait ajoutée)
      // On attend un tick pour que React mette à jour periods
      setTimeout(() => {
        const updatedPeriods = [...periods, newPeriod].sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
        const newIndex = updatedPeriods.findIndex(p => p.id === newPeriod.id);
        if (newIndex !== -1) {
          setCurrentPeriodIndex(newIndex);
        }
      }, 100);
      
      setNewPeriodName('');
    } catch (error) {
      console.error('Error adding period:', error);
    }
  }
  

  async function deletePeriod(periodId) {
    if (!currentSession) return;
    const period = currentSession.periods.find((p) => p.id === periodId);
    if (!period) return;
    if (!window.confirm(`Supprimer la période '${period.name}' ?`)) return;

    try {
      // ✅ La mise à jour UI sera faite automatiquement par handleWebSocketMessage
      await wsAction.deletePeriod(
        wsSend,
        { period_id: periodId },
        user.access_token
      );
      
      // ✅ Ajuster l'index si nécessaire après suppression
      const newPeriodsLength = currentSession.periods.filter(p => p.id !== periodId).length;
      if (currentPeriodIndex >= newPeriodsLength) {
        setCurrentPeriodIndex(Math.max(0, newPeriodsLength - 1));
      }
    } catch (error) {
      console.error('Error deleting period:', error);
    }
  }

  // -----------------------------
  // 11) Optimization
  // -----------------------------
  async function runOptimization() {
    if (!currentSession) return;

    // ✅ Vérification : Position requiert plusieurs skills qu'aucun danseur ne possède simultanément
    for (const period of periods) {
      for (const pos of period.positions || []) {
        if (pos.skills && pos.skills.length > 1) {
          const requiredSkillIds = new Set(pos.skills.map(s => s.id));
          const hasDancerWithAllSkills = dancers.some(dancer => {
            const dancerSkillIds = new Set(dancer.skills?.map(s => s.id) || []);
            return [...requiredSkillIds].every(skillId => dancerSkillIds.has(skillId));
          });
          
          if (!hasDancerWithAllSkills) {
            const skillNames = pos.skills.map(s => s.name).join(', ');
            showLinkError(
              `Impossible d'optimiser : Dans la période '${period.name}', une position requiert les skills [${skillNames}], mais aucun danseur ne possède tous ces skills simultanément.`,
              6000
            );
            return;
          }
        }
      }
    }

    // ✅ Vérification des conflits entre cadenas et liens
    if (forcedPosition) {
      // Récupérer tous les groupes de liens
      const linkGroupsMap = new Map(); // linkGroupId -> [positions]
      
      periods.forEach(period => {
        period.positions.forEach(pos => {
          if (pos.link_group_id) {
            if (!linkGroupsMap.has(pos.link_group_id)) {
              linkGroupsMap.set(pos.link_group_id, []);
            }
            linkGroupsMap.get(pos.link_group_id).push(pos);
          }
        });
      });

      // Vérifier chaque groupe de liens
      for (const [linkGroupId, linkedPositions] of linkGroupsMap) {
        // Récupérer les positions qui ont un dancer_id assigné
        const assignedPositions = linkedPositions.filter(pos => pos.dancer_id !== null);
        
        if (assignedPositions.length > 0) {
          // Vérifier si tous les danseurs assignés sont les mêmes
          const dancerIds = new Set(assignedPositions.map(pos => pos.dancer_id));
          
          if (dancerIds.size > 1) {
            // Conflit détecté : plusieurs danseurs différents dans un même groupe de liens
            const dancerNames = Array.from(dancerIds)
              .map(dancerId => dancers.find(d => d.id === dancerId)?.name || 'Inconnu')
              .join(', ');
            
            showLinkError(
              `Conflit : Le cadenas est activé mais des positions liées sont assignées à différents danseurs (${dancerNames}). Veuillez corriger avant d'optimiser.`,
              6000
            );
            return;
          }
        }
      }
    }
  
    setIsOptimizing(true);  // Active l'overlay
    setElapsedTime(0);      // Reset le timer
  
    const startTime = Date.now();
    const timeLimit = 30; // secondes (peut être modifié)
  
    // Créer un AbortController pour pouvoir annuler la requête
    const abortController = new AbortController();
    optimizationAbortControllerRef.current = abortController;
  
    // Timer qui met à jour `elapsedTime` toutes les 100 ms
    const timerInterval = setInterval(() => {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      setElapsedTime(elapsed);
      
      // ✅ Sécurité : arrêter après 30.5 secondes côté client aussi
      if (parseFloat(elapsed) >= 30) {
        clearInterval(timerInterval);
        abortController.abort();
      }
    }, 100);
    optimizationTimerRef.current = timerInterval;
  
    try {
      console.log(forcedPosition);
      const optimizationResult = await sessionApi.runOptimization(
        currentSession.id,
        timeLimit,
        100000,
        0.9999,
        forcedPosition,
        abortController.signal
      );
      
      const updatedSession = await sessionApi.fetchSession(currentSession.id, user.access_token);
    
      // Tri des périodes par start_time pour l'ordre chronologique
      const sortedPeriods = (updatedSession.periods || []).slice().sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
    
    
      setCurrentSession({
        ...updatedSession,
        periods: sortedPeriods,
      });
      setPeriods(sortedPeriods);
      setDancers(updatedSession.dancers);
      setSkills(updatedSession.skills);
      
      // Afficher le message de succès
      setShowOptimizationSuccess(true);
      setOptimizationError(null);
    
      // Reste du code...
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('Optimisation annulée par l\'utilisateur');
        if (errorTimerRef.current) {
          clearTimeout(errorTimerRef.current);
          errorTimerRef.current = null;
        }
        setOptimizationError(null);
      } else {
        console.error('Erreur lors de l\'optimisation :', err);
        // Nettoyer le timer précédent s'il existe
        if (errorTimerRef.current) {
          clearTimeout(errorTimerRef.current);
        }
        // Extraire le message d'erreur du backend
        const errorMessage = err.response?.data?.detail || "Erreur inconnue lors de l'optimisation";
        setOptimizationError({ message: errorMessage, timestamp: Date.now() });
        // Créer un nouveau timer
        errorTimerRef.current = setTimeout(() => {
          setOptimizationError(null);
          errorTimerRef.current = null;
        }, 10000);
      }
    } finally {
      clearInterval(timerInterval);
      optimizationTimerRef.current = null;
      optimizationAbortControllerRef.current = null;
      setIsOptimizing(false);
      // Réinitialiser le message de succès après un délai
      setTimeout(() => setShowOptimizationSuccess(false), 3500);
    }
    
  }

  // -----------------------------
  // 12) Switch de vue
  // -----------------------------
  function toggleViewMode() {
    setViewMode((prev) => (prev === 'scene' ? 'trajectory' : 'scene'));
  }

  // ==============================
  // UPLOAD MUSIQUE
  // ==============================

  // Empêcher le comportement par défaut pour autoriser le drop
  function handleMusicDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(true);
  }
 
  function handleMusicDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(false);
  }
 
  async function handleMusicDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setMusicIsDragging(false);
 
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      await handleMusicFile(file);
    }
  }
 
  // Ouverture du sélecteur de fichier via un click
  function handleMusicClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }
 
  // Quand l'utilisateur choisit un fichier via <input type="file" />
  async function handleMusicFileInputChange(e) {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      await handleMusicFile(file);
    }
  }
 
  // Vérifie la validité (taille, type, etc.) puis envoie la musique au backend
  async function handleMusicFile(file) {
    console.log("Fichier audio sélectionné :", file);
  
    if (!file.type.startsWith("audio/")) {
      alert("Fichier non supporté. Merci de sélectionner un fichier audio.");
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) {
      alert("Fichier trop volumineux. Maximum 10 Mo.");
      return;
    }
  
    try {
      const uploadedMusic = await sessionApi.uploadMusic(currentSession.id, file, user.access_token);
  
      console.log("Upload réussi:", uploadedMusic);
      alert("Musique uploadée avec succès !");
  
      // 🔥 Rafraîchir la session pour charger la nouvelle musique
      const updatedSession = await sessionApi.fetchSession(currentSession.id, user.access_token);
  
      // Tri des périodes par start_time
      const sortedPeriods = (updatedSession.periods || []).slice().sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
      setCurrentSession({ ...updatedSession, periods: sortedPeriods });
      setPeriods(sortedPeriods);
  
    } catch (err) {
      console.error("Erreur upload:", err);
      alert("Erreur lors de l'upload de la musique.");
    }
  }
  
  // ==============================
  // LECTURE dans StageView.jsx
  // ==============================


  // Ajoutez ce useEffect pour stopper la musique lors du démontage du composant
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        console.log("Audio stoppé lors du démontage du composant.");
      }
    };
  }, []);

  // Synchroniser currentTime avec la progression naturelle de l'audio
  useEffect(() => {
    if (audioRef.current) {
      const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime);
      };
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [audioRef.current]);

  useEffect(() => {
    if (currentSession && currentSession.musics && currentSession.musics.length > 0) {
      const musicUrl = `${API_BASE_URL}/musics/${currentSession.musics[0].id}/file`;
      // Mettre à jour la source uniquement si elle a changé
      if (!audioRef.current) {
        audioRef.current = new Audio(musicUrl);
        audioRef.current.crossOrigin = 'anonymous';
      } else if (audioRef.current.src !== musicUrl) {
        audioRef.current.src = musicUrl;
        audioRef.current.crossOrigin = 'anonymous';
      }
      
      // Synchroniser la position audio avec currentTime lors du chargement
      // Attendre que l'audio soit chargé avant de modifier currentTime
      const syncAudioTime = () => {
        if (audioRef.current && currentTime > 0) {
          audioRef.current.currentTime = currentTime;
        }
      };
      
      if (audioRef.current.readyState >= 2) {
        // Audio déjà chargé
        syncAudioTime();
      } else {
        // Attendre que l'audio soit chargé
        audioRef.current.addEventListener('loadedmetadata', syncAudioTime, { once: true });
      }
    }
  }, [currentSession]);
  

  // Contrôle de la lecture en fonction de isPlaying
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        // Laisse l'audio avancer naturellement
        audioRef.current.play().catch((err) => {
          console.error("Erreur lors du play :", err);
        });
      } else {
        audioRef.current.pause();
        console.log("Pause");
      }
    }
  }, [isPlaying]);

  // si on est entre le début/fin d'une periode alors on la selectionne
  useEffect(() => {
    if (!periods || periods.length === 0) return;
    // Ne considérer que les périodes avec start_time et end_time définis
    const index = periods.findIndex(period =>
      period.start_time != null &&
      period.end_time != null &&
      currentTime >= period.start_time &&
      currentTime <= period.end_time
    );
    if (index !== -1 && index !== currentPeriodIndex) {
      setCurrentPeriodIndex(index);
    }
  }, [currentTime, periods, currentPeriodIndex]);
  


  // ==============================
  // CONTROL BAR 
  // ==============================

  // Placez ce useEffect au niveau supérieur de votre composant (avec les autres useEffect)
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  
  // -----------------------------
  // PDF DOWNLOAD (via utilitaire)
  // -----------------------------

  const handleDownloadPdf = async () => {
    // Lancer la génération du PDF en arrière-plan sans bloquer l'UI
    try {
      await generateChoreographyPDF({
        currentSession,
        dancers,
        periods,
        skills,
        canvasPixelWidth,
        canvasPixelHeight,
        drawGrid,
        drawPosition,
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    }
  };
  

  // -----------------------------
  // 13) Rendu final
  // -----------------------------
  return (
    <div
      style={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Barre latérale */}
      <div
        style={{
          width: `${SIDEBAR_WIDTH}px`,
          background: "#f2f2f2",
          borderRight: '1px solid #d1d1d6',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Zone fixe contenant le logo et le bouton */}
        <div 
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px",
            background: "linear-gradient(135deg, #ffd6dd,rgb(255, 248, 249))",
            borderBottom: "1px solid #d1d1d6",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: "40px",  
          }}
          className="nav-bar"
        >

          {/* Zone gauche (Logo) */}
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-start",
              paddingRight: "10px",
              height: "100%",
              position: "relative",
              cursor: "pointer",
            }}
            className="left-section"
            onClick={() => navigate("/")}
          >
            {/* ✅ Indicateur WebSocket avec Tooltip */}
            <Tooltip 
              text={wsConnected 
                ? `Connecté${connectionCount > 1 ? ` - ${connectionCount - 1} autre${connectionCount > 2 ? 's' : ''} utilisateur${connectionCount > 2 ? 's' : ''}` : ''}`
                : 'Déconnecté'
              }
              delay={500}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: wsConnected ? '#34c759' : '#ff3b30',
                  marginRight: '10px',
                  boxShadow: wsConnected ? '0 0 8px rgba(52, 199, 89, 0.6)' : '0 0 8px rgba(255, 59, 48, 0.6)',
                  transition: 'all 0.3s ease',
                }}
              />
            </Tooltip>
            <span 
              style={{
                fontSize: "1.7rem",
                fontFamily: "'Shirkhand', cursive",
                color: "#ff477e",
                fontWeight: "bold",
                transformOrigin: "left",
                transition: "transform 0.2s ease-in-out", // Transition uniquement sur l'agrandissement
              }} 
              className="dansy"
              onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
            >
              Dansy
            </span>
          </div>


          {/* Zone droite (Bouton Mes sessions) */}
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "flex-end",
              paddingLeft: "10px",
              height: "100%",
              position: "relative",
            }}
            className="right-section"
          >
            <button
              onClick={() => navigate('/sessions')}
              style={{
                padding: "6px 30px",
                height: "100%",
                borderRadius: "0px",
                border: "none",
                backgroundColor: "transparent",
                color: "#242323",
                cursor: "pointer",
                fontSize: "0.8rem",
                transition: "transform 0.2s ease-in-out", // Transition uniquement sur l'agrandissement
              }}
              className="retour"
              onMouseEnter={(e) => e.target.style.transform = "scale(1.08)"}
              onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
            >
              Mes sessions
            </button>
          </div>

        </div>
        
        {/* Nom de la session */}
        {currentSession && (
          <div 
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              padding: "6px",
              background: "#e9e9e9",
              borderBottom: "1px solid #d1d1d6",
            }}
          >
            <span
              title={currentSession.name}
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentSession.name}
            </span>
            <button
              onClick={() => setShowShareModal(true)}
              title="Partager la session"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#d0d0d0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <IoShareOutline size={20} color="#555" />
            </button>
          </div>
        )}

        {/* Contenu de la barre latérale */}
        <div style={{ padding: '1rem' }}>

          {currentSession && (
            <SectionList
              title="Périodes"
              data={[...periods].sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity))}
              selectedId={periods[currentPeriodIndex]?.id}
              onSelect={(id) => {
                const newIndex = periods.findIndex((p) => p.id === id);
                setCurrentPeriodIndex(newIndex);
                const selectedPeriod = periods[newIndex];
                if (selectedPeriod && selectedPeriod.start_time != null) {
                  jumpToTimeAndPause(selectedPeriod.start_time);
                } else {
                  setCurrentTime(null);
                }
              }}
              
              onAdd={addPeriod}
              addPlaceholder="Nom de la période"
              addValue={newPeriodName}
              setAddValue={setNewPeriodName}
              onDelete={(id) => deletePeriod(id)}
              renderItem={(period) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0, // Important pour permettre le flex shrink
                  }}
                >
                  {/* Nom de la période à gauche */}
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: '1',
                      minWidth: 0,
                    }}
                    title={period.name}
                  >
                    {period.name}
                  </div>
                  {/* Timestamp formaté à droite */}
                  <div style={{ marginLeft: 'auto', fontSize: '0.73rem', color: '#555', flexShrink: 0 }}>
                    {formatTimestamp(period.start_time)} &gt; {formatTimestamp(period.end_time)}
                  </div>
                </div>
              )}
            />
          )}




          {currentSession && (
            <SectionList
              title="Danseurs"
              titleExtra={<SortButton sortMode={dancersSort} setSortMode={setDancersSort} />}
              data={sortData(dancers, dancersSort)}
              selectedId={selectedDancerId}
              onSelect={handleDancerSelect}
              onAdd={addDancer}
              addPlaceholder="Nom du danseur"
              addValue={newDancerName}
              setAddValue={setNewDancerName}
              onDelete={deleteDancer}
              onRightClick={handleDancerRightClick}
              renderItem={(dancer) => (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
                  {/* Nom du dancer à gauche */}
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: '1',
                      minWidth: 0,
                      marginRight: '8px',
                    }}
                    title={dancer.name}
                  >
                    {dancer.name}
                  </span>
                  {/* Étoiles alignées à droite pour ses skills */}
                  <span 
                    style={{ 
                      flexShrink: 1,
                      marginLeft: '4px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      maxWidth: '40%',
                    }}
                    title={dancer.skills?.map(sk => sk.name).join(', ') || ''}
                  >
                    {dancer.skills?.map((sk) => (
                      <span key={sk.id} style={{ color: sk.color, marginLeft: '2px' }}>★</span>
                    ))}
                  </span>
                </span>
              )}
            />
          )}

          {currentSession && (
            <SectionList
              title="Skills"
              titleExtra={<SortButton sortMode={skillsSort} setSortMode={setSkillsSort} />}
              data={sortData(skills, skillsSort)}
              selectedId={selectedSkillId}
              onSelect={setSelectedSkillId}
              onAdd={addSkill}
              addPlaceholder="Nom du skill"
              addValue={newSkillName}
              setAddValue={setNewSkillName}
              onDelete={deleteSkill}
              extraInput={(
                <input 
                  type="color" 
                  value={newSkillColor} 
                  onChange={(e) => setNewSkillColor(e.target.value)} 
                  style={{borderRadius: '100%', width: '2.5rem', height: '2rem', border: 'none', cursor: 'pointer', marginLeft: '0px', alignItems: 'center' }}
                />
              )}
              renderItem={(skill) => (
                <span style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                  <span
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: skill.color,
                      display: 'inline-block',
                      marginRight: '8px',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: '1',
                      minWidth: 0,
                    }}
                    title={skill.name}
                  >
                    {skill.name}
                  </span>
                </span>
              )}
            />

          )}

          {currentSession && (
            <StuntGroupList
              sessionId={sessionId}
              dancers={dancers}
              stunts={stunts}
              stuntGroups={currentSession.stunt_groups || []}
              wsSend={wsSend}
              user={user}
              sortMode={stuntGroupsSort}
              onSortChange={setStuntGroupsSort}
              onStuntGroupsChange={async () => {
                // Recharger uniquement les stunt_groups
                const sessionData = await sessionApi.fetchSession(sessionId, user.access_token);
                setCurrentSession(prev => ({
                  ...prev,
                  stunt_groups: sessionData.stunt_groups || [],
                }));
              }}
              onStuntsChange={async () => {
                // Recharger uniquement les stunts
                const sessionData = await sessionApi.fetchSession(sessionId, user.access_token);
                setStunts(sessionData.stunts || []);
                // Mettre à jour aussi stunt_groups car ils contiennent les stunts
                setCurrentSession(prev => ({
                  ...prev,
                  stunt_groups: sessionData.stunt_groups || [],
                }));
              }}
              onGroupSelect={(group) => {
                // Groupe sélectionné
              }}
            />
          )}

          <hr
            style={{
              margin: '1rem 0',
              border: 'none',
              borderTop: '1px solid #d1d1d6',
            }}
          />
        </div>
      </div>

      {/* Partie droite */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f3f5',
          padding: `${MIN_MARGIN}px`,
          boxSizing: 'border-box',
        }}
      >
        {currentSession ? (
          <div
            style={{
              width: `${containerWidth}px`,
              height: `${containerHeight}px`,
              backgroundColor: '#ffffff',
              border: '1px solid #d1d1d6',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
              {/* Barre du haut */}
              <TopBar
                viewMode={viewMode}
                toggleViewMode={toggleViewMode}
                handleDownloadPdf={handleDownloadPdf}
                cursorMode={cursorMode}
                setCursorMode={setCursorMode}
                selectedSkillId={selectedSkillId}
                skills={skills}
                runOptimization={runOptimization}
                forcedPosition={forcedPosition}
                setForcedPosition={setForcedPosition}
                sessionErrors={sessionErrors}
                showErrorMenu={showErrorMenu}
                setShowErrorMenu={setShowErrorMenu}
              />

            {/* Canvas */}
            <div
              onMouseMove={(e) => {
                // Détecter si la souris est sur la zone du message d'info en mode link
                if (isLinkModeActive) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const mouseX = e.clientX - rect.left;
                  const mouseY = e.clientY - rect.top;
                  
                  // Zone du message : top: 10px, left: 10px, minWidth: 260px, padding: 8px 12px
                  // Hauteur approximative : ~60px
                  const isOverMessage = 
                    mouseX >= 10 && 
                    mouseX <= 10 + 260 + 24 && // 260px + padding (12px * 2)
                    mouseY >= 10 && 
                    mouseY <= 10 + 70; // hauteur approximative
                  
                  setIsLinkInfoHovered(isOverMessage);
                }
              }}
              style={{
                position: 'relative',
                width: '100%',
                height: `${canvasHeight}px`,
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <canvas
                ref={canvasRef}
                width={canvasPixelWidth}
                height={canvasPixelHeight}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  cursor:
                    cursorMode === 'default'
                      ? 'crosshair'
                      : cursorMode === 'circle'
                      ? 'crosshair' // ou un 'url(...)' si tu as un PNG
                      : 'crosshair', // idem pour star
                }}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={onCanvasMouseUp}
                onContextMenu={onCanvasContextMenu}
              />

              {/* Si vue aucun danseur et vue trajectoire activée  */}
              {(!periods || periods.length > 0) && viewMode === 'trajectory' && (!selectedDancerId || selectedDancerId === null) && (!dancers || dancers.length === 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #c1121f',
                    borderRadius: '8px',
                    color: '#c1121f',
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                    zIndex: 10,
                  }}
                >
                  Veuillez ajouter un danseur pour voir sa trajectoire
                </div>
              )}

              {/* Affichage des erreurs */}
              <ErrorDisplay 
                showErrorMenu={showErrorMenu} 
                sessionErrors={sessionErrors} 
                periods={periods}
                canvasHeight={canvasHeight}
                onPeriodClick={(periodIndex) => {
                  setCurrentPeriodIndex(periodIndex);
                  const selectedPeriod = periods[periodIndex];
                  if (selectedPeriod && selectedPeriod.start_time != null) {
                    jumpToTimeAndPause(selectedPeriod.start_time);
                  }
                }}
                onClose={() => setShowErrorMenu(false)}
                onLinkErrorClick={(periodIndices) => {
                  // Basculer en mode link
                  setCursorMode('link');
                  
                  // Récupérer toutes les positions des périodes concernées qui ont le même link_group_id
                  const linkGroupIds = new Set();
                  periodIndices.forEach(pIdx => {
                    const period = periods[pIdx];
                    period.positions?.forEach(pos => {
                      if (pos.link_group_id) {
                        linkGroupIds.add(pos.link_group_id);
                      }
                    });
                  });
                  
                  // Sélectionner la première chaîne de liens trouvée
                  if (linkGroupIds.size > 0) {
                    const firstLinkGroupId = Array.from(linkGroupIds)[0];
                    const linkedPositions = [];
                    
                    periods.forEach(period => {
                      period.positions?.forEach(pos => {
                        if (pos.link_group_id === firstLinkGroupId) {
                          linkedPositions.push(pos);
                        }
                      });
                    });
                    
                    // Utiliser setLinkChainDirectly pour définir la chaîne
                    if (linkedPositions.length > 0) {
                      setLinkChainDirectly(linkedPositions);
                      // Naviguer vers la première période de la chaîne
                      const firstPeriodIndex = periodIndices[0];
                      setCurrentPeriodIndex(firstPeriodIndex);
                      const selectedPeriod = periods[firstPeriodIndex];
                      if (selectedPeriod && selectedPeriod.start_time != null) {
                        jumpToTimeAndPause(selectedPeriod.start_time);
                      }
                    }
                  }
                }}
              />

              {/* ✅ Notification d'erreur pour le mode link */}
              {linkError && (
                <div
                  style={{
                    position: 'absolute',
                    top: linkErrorVisible ? '10px' : '-100px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '10px 16px',
                    backgroundColor: '#fff',
                    border: '2px solid #f44336',
                    borderRadius: '8px',
                    color: '#d32f2f',
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                    fontWeight: '500',
                    zIndex: 50,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    opacity: linkErrorVisible ? 1 : 0,
                    transition: 'top 0.3s ease-in-out, opacity 0.3s ease-in-out',
                    minWidth: '300px',
                  }}
                >
                  <span style={{ flex: 1 }}>{linkError}</span>
                  <button
                    onClick={closeLinkError}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#d32f2f',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '0',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* ✅ Message d'info en mode link */}
              {isLinkModeActive && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '8px 12px',
                    backgroundColor: isLinkInfoHovered 
                      ? 'rgba(255, 255, 255, 0.3)' 
                      : 'rgba(255, 255, 255, 0.7)',
                    border: `1px solid ${isLinkInfoHovered 
                      ? 'rgba(76, 175, 80, 0.2)' 
                      : 'rgba(76, 175, 80, 0.5)'}`,
                    borderRadius: '6px',
                    color: isLinkInfoHovered 
                      ? 'rgba(46, 125, 50, 0.4)' 
                      : 'rgba(46, 125, 50, 0.85)',
                    fontSize: '12px',
                    fontFamily: 'sans-serif',
                    zIndex: 15,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    minWidth: '260px',
                    pointerEvents: 'none',
                    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out',
                  }}
                >
                  <div style={{ 
                    fontWeight: '600', 
                    marginBottom: '4px', 
                    opacity: isLinkInfoHovered ? 0.4 : 0.9,
                    transition: 'opacity 0.2s ease-in-out',
                  }}>
                    Mode Liaison - {linkChain.length} position{linkChain.length > 1 ? 's' : ''} sélectionnée{linkChain.length > 1 ? 's' : ''}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    opacity: isLinkInfoHovered ? 0.4 : 0.8,
                    transition: 'opacity 0.2s ease-in-out',
                  }}>
                    {linkChain.length === 0 && 'Cliquez sur les positions à lier ensemble'}
                    {linkChain.length === 1 && 'Sélectionnez d\'autres positions (de périodes différentes)'}
                    {linkChain.length >= 2 && (
                      <>
                        Appuyez sur <strong>Entrée</strong> pour valider le lien
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Si aucune période n'est crée  */}
              { (!periods || periods.length === 0) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #c1121f',
                    borderRadius: '8px',
                    color: '#c1121f',
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                    zIndex: 10,
                  }}
                >
                  Aucune période existante
                </div>
              )}


              {contextMenuPosition?.visible && (
                <ContextMenu
                  x={contextMenuPosition.x}
                  y={contextMenuPosition.y}
                  options={contextMenuPosition.options}
                  onClose={() =>
                    setContextMenuPosition({
                      ...contextMenuPosition,
                      visible: false,
                      useFixed: false,
                    })
                  }
                  useFixed={contextMenuPosition.useFixed ?? false}
                />
              )}
              
              {dancerContextMenu.visible && (
                <ContextMenu
                  x={dancerContextMenu.x}
                  y={dancerContextMenu.y}
                  options={dancerContextMenu.options}
                  onClose={() =>
                    setDancerContextMenu({
                      ...dancerContextMenu,
                      visible: false,
                    })
                  }
                  useFixed={dancerContextMenu.useFixed ?? true}
                />
              )}

              {/* Tooltip au survol */}
              {tooltip.visible && (
                <div
                  style={{
                    position: 'fixed',
                    left: `${tooltip.x}px`,
                    top: tooltip.position === 'above' ? `${tooltip.y - 8}px` : `${tooltip.y + 8}px`,
                    transform: tooltip.position === 'above' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: '#fff',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    opacity: 1,
                    transition: 'opacity 0.3s ease-in-out',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    animation: 'fadeIn 0.3s ease-in-out',
                  }}
                >
                  {tooltip.text}
                </div>
              )}
            </div>

            {/* Barre de controle */}
            <div className="control-bar" style={{ height: `${CONTROL_BAR_HEIGHT}px` }}>
            {/* Partie gauche : Timestamp */}
            <div className="timestamp">
              <span>
                {formatTimestamp(currentTime)} / {audioRef.current && audioRef.current.duration ? formatTimestamp(audioRef.current.duration) : '--:--'}
              </span>
            </div>


              {/* Partie centrale : Contrôles de lecture */}
              <div className="controls">
                {/* Bouton pour revenir à la première période */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (periods.length > 0) {
                      setCurrentPeriodIndex(0);
                      const firstPeriod = periods[0];
                      if (firstPeriod) {
                        jumpToTimeAndPause(firstPeriod.start_time);
                      }
                    }
                  }}
                >
                  {'⏮'}
                </button>

                {/* Bouton pour reculer de 1 seconde */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (audioRef.current) {
                      const newTime = Math.max(audioRef.current.currentTime - 1, 0);
                      jumpToTimeAndPause(newTime);
                      
                      // ✅ Mettre à jour currentPeriodIndex pour éviter le flash
                      const newPeriodIndex = periods.findIndex(
                        (period) =>
                          newTime >= period.start_time - 0.01 &&
                          newTime <= period.end_time + 0.01
                      );
                      if (newPeriodIndex !== -1) {
                        setCurrentPeriodIndex(newPeriodIndex);
                      }
                    }
                  }}
                >
                  {'⏴'}
                </button>

                {/* Bouton Play/Pause (reste inchangé) */}
                <button
                  className={`control-btn play-btn ${isPlaying ? 'active' : ''}`}
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? '⏸' : '⏵'}
                </button>

                {/* Bouton pour avancer de 1 seconde */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (audioRef.current) {
                      const newTime = Math.min(
                        audioRef.current.currentTime + 1,
                        audioRef.current.duration || Infinity
                      );
                      jumpToTimeAndPause(newTime);
                      
                      // ✅ Mettre à jour currentPeriodIndex pour éviter le flash
                      const newPeriodIndex = periods.findIndex(
                        (period) =>
                          newTime >= period.start_time - 0.01 &&
                          newTime <= period.end_time + 0.01
                      );
                      if (newPeriodIndex !== -1) {
                        setCurrentPeriodIndex(newPeriodIndex);
                      }
                    }
                  }}
                >
                  {'⏵'}
                </button>

                {/* Bouton pour aller à la dernière période */}
                <button
                  className="control-btn"
                  onClick={() => {
                    if (periods.length > 0) {
                      const lastIndex = periods.length - 1;
                      setCurrentPeriodIndex(lastIndex);
                      const lastPeriod = [...periods].reverse().find(period => period.end_time !== null);
                      if (lastPeriod) {
                        jumpToTimeAndPause(lastPeriod.start_time);
                      }
                    }
                  }}
                >
                  {'⏭'}
                </button>
              </div>



              {/* Partie droite : Volume, Fullscreen et Toggle 3D/2D */}
              <div className="extra-controls">
                <div className="divider"></div>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  className="volume-slider" 
                  value={volume * 100}
                  onChange={(e) => {
                    const vol = e.target.value / 100;
                    setVolume(vol);
                    if (audioRef.current) {
                      audioRef.current.volume = vol;
                    }
                  }}
                  onMouseUp={(e) => e.target.blur()}
                  onTouchEnd={(e) => e.target.blur()}
                />

                <div className="divider"></div>

                {/* Bouton 3D */}
                <button 
                  className={`toggle-btn threeD-btn ${isThreeDMode ? 'active' : ''}`} 
                  onClick={() => setIsThreeDMode(!isThreeDMode)}
                >
                  {isThreeDMode ? '3D' : '2D'}
                </button>

                <div className="divider"></div>

                {/* Bouton Fullscreen */}
                <button 
                  className={`toggle-btn ${isFullscreen ? 'active' : ''}`} 
                  onClick={() => {
                    if (!document.fullscreenElement) {
                      // Demande le mode plein écran sur l'élément désiré, ici on prend document.documentElement
                      document.documentElement.requestFullscreen().catch(err => {
                        console.error("Erreur lors de la demande du mode plein écran :", err);
                      });
                      setIsFullscreen(true);
                    } else {
                      document.exitFullscreen().catch(err => {
                        console.error("Erreur lors de la sortie du mode plein écran :", err);
                      });
                      setIsFullscreen(false);
                    }
                  }}
                >
                  {'⛶'}
                </button>

              </div>
            </div>

            <div
              style={{
                width: '100%',
                height: `${BOTTOM_BAR_HEIGHT - CONTROL_BAR_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#efefef',
                borderTop: '1px solid #d1d1d6',
                overflow: 'hidden',
                position: 'relative', // Permet de positionner la croix
              }}
            >
              {/* Bouton de fermeture rectangulaire dans l'angle supérieur droit */}
                {currentSession?.musics.length > 0 && (
                <div
                  onClick={async () => {
                    if (!currentSession || currentSession.musics.length === 0) return;
                  
                    const musicId = currentSession.musics[0].id; // Supposons qu'il n'y ait qu'une seule musique
                  
                    try {
                      await sessionApi.deleteMusic(currentSession.id, musicId, user.access_token);
                    
                      // Mise à jour du state pour retirer la musique et supprimer les timestamps des périodes
                      const updatedSession = {
                        ...currentSession,
                        musics: [],
                        periods: currentSession.periods.map(period => ({
                          ...period,
                          start_time: null,
                          end_time: null,
                        })),
                      };
                      setCurrentSession(updatedSession);
                      setPeriods(updatedSession.periods); // ✅ Mettre à jour le state periods
                      alert("Musique dissociée avec succès !");
                    } catch (error) {
                      console.error("Erreur lors de la dissociation de la musique :", error);
                      alert("Impossible de dissocier la musique.");
                    }
                  }}
                  
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: `${amplitudeSliderWidth}px`,
                    height: `${timelineHeight}px`,
                    backgroundColor: '#efefef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                    zIndex: 5, // Pour passer au-dessus de la timeline
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = '#ddd';
                  }}
                  
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                    e.currentTarget.style.backgroundColor = '#efefef';
                  }}
                >
                  <span style={{ fontSize: '16px', color: '#888' }}>✕</span>
                </div>
              )}

              {currentSession?.musics?.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {currentSession.musics.map((music) => (
                    <div key={music.id} style={{ flex: 1, height: '100%' }}>
                      
                      
                      <MusicSpectrogram
                        music={music}
                        sessionId={currentSession.id}
                        user={user}
                        periods={periods}
                        currentPeriodIndex={currentPeriodIndex}
                        bottomBarHeight={BOTTOM_BAR_HEIGHT}
                        controlBarHeight={CONTROL_BAR_HEIGHT}
                        currentTime={currentTime}
                        onTimeUpdate={setCurrentTime}
                        audioRef={audioRef}
                        onStopAnimation={stopAnimation}
                        wsSend={wsSend}
                        onPeriodUpdate={(update) => {
                          // Si c'est juste une sélection, mettre à jour l'index et sortir
                          if (update && update.select) {
                            setCurrentPeriodIndex(update.periodIndex);
                            return;
                          }
                          
                          // Si une période est déplacée/modifiée pendant qu'on joue une animation
                          if (update && update.update) {
                            // Stopper l'animation si elle est en cours
                            if (animationFrameRef.current) {
                              cancelAnimationFrame(animationFrameRef.current);
                              animationFrameRef.current = null;
                            }
                            // Se positionner au début de la période déplacée et mettre en pause
                            if (update.newStart != null) {
                              jumpToTimeAndPause(update.newStart);
                            }
                          }
                          
                          // Les données sont mises à jour automatiquement via le handler WebSocket
                          // (period_created, period_updated), pas besoin de refaire un GET
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  onDragOver={handleMusicDragOver}
                  onDragLeave={handleMusicDragLeave}
                  onDrop={handleMusicDrop}
                  onClick={handleMusicClick}
                  style={{
                    flex: 1,
                    maxWidth: '90%',
                    height: '60px',
                    backgroundColor: musicIsDragging ? '#fff0f3' : '#f0f0f5',
                    border: '2px dashed #ccc',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                >
                  {musicIsDragging
                    ? 'Déposez votre fichier audio...'
                    : 'Glissez une musique ou cliquez ici pour l\'ajouter. (10 Mo max)'
                  }
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept="audio/*"
                    onChange={handleMusicFileInputChange}
                  />
                </div>
              )}
            </div>

          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              color: '#6c6c6c',
              fontSize: '18px',
              padding: '20px',
            }}
          >
            Chargement de la session...
          </div>
        )}
      </div>
      
      {/* Overlay d'optimisation */}
      <OptimizationOverlay 
        isOptimizing={isOptimizing} 
        elapsedTime={elapsedTime}
        showSuccess={showOptimizationSuccess}
        errorMessage={optimizationError}
      />

      {/* Curseurs des autres utilisateurs (collaboration temps réel) */}
      <RemoteCursors
        remoteCursors={remoteCursors}
        currentPeriodId={currentPeriodId}
        canvasWidth={canvasRealSize.width || canvasPixelWidth}
        canvasHeight={canvasRealSize.height || canvasPixelHeight}
        canvasOffsetX={canvasOffset.x}
        canvasOffsetY={canvasOffset.y}
        currentViewMode={viewMode}
        currentTime={currentTime}
        periods={periods}
      />

      {/* Modal de partage */}
      {showShareModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowShareModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '32px',
              minWidth: '400px',
              maxWidth: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔗</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '600' }}>Partager la session</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '15px' }}>
              {currentSession?.name}
            </p>
            
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <input
                type="email"
                placeholder="Email de l'invité..."
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendInvitation()}
                style={{
                  flex: 1,
                  padding: '14px 18px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff477e'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
              <button
                onClick={handleSendInvitation}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#ff477e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e63e7a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff477e'}
              >
                Envoyer
              </button>
            </div>
            
            {inviteError && (
              <p style={{ color: '#ff4d4d', fontSize: '14px', margin: '0 0 16px 0' }}>{inviteError}</p>
            )}
            {inviteSuccess && (
              <p style={{ color: '#4CAF50', fontSize: '14px', margin: '0 0 16px 0' }}>{inviteSuccess}</p>
            )}
            
            {/* Invitations en attente */}
            {sentInvitations.length > 0 && (
              <div style={{ marginTop: '8px', marginBottom: '16px', textAlign: 'left' }}>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px', fontWeight: '500' }}>
                  Invitations en attente :
                </p>
                {sentInvitations.map(inv => (
                  <div 
                    key={inv.id} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      backgroundColor: '#f8f8f8',
                      borderRadius: '10px',
                      marginBottom: '8px',
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {inv.invitee_email}
                    </span>
                    <button
                      onClick={() => handleCancelInvitation(inv.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        border: '1px solid #ff4d4d',
                        borderRadius: '8px',
                        color: '#ff4d4d',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ff4d4d';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#ff4d4d';
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <button
              onClick={() => setShowShareModal(false)}
              style={{
                padding: '10px 24px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                color: '#666',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

    </div>
    
  );
}
